import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.dashboard');
import { OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

type ActivationMap = { [key: string]: number };
type ReasonMap = { [key: string]: string };

interface FocusProcessingProfile {
    features: string;
    form: string;
    memory: string;
    action: string;
}

interface StimulusFocusPoint {
    id: string;
    label: string;
    cue: string;
    description: string;
    processing: FocusProcessingProfile;
    x: number;
    y: number;
    activationDeltas: ActivationMap;
}

interface VisualStimulus {
    id: string;
    label: string;
    subtitle: string;
    category: string;
    imageSrc: string;
    imageAlt: string;
    signal: string;
    summary: string;
    task: string;
    elements: string[];
    focusPoints: StimulusFocusPoint[];
    activations: ActivationMap;
    reasons: ReasonMap;
}

interface BrainRegion {
    id: string;
    name: string;
    english: string;
    short: string;
    network: string;
    location: string;
    function: string;
    evidenceId: string;
    lateralX: number;
    lateralY: number;
    dorsalX: number;
    dorsalY: number;
    pathwayX: number;
    pathwayY: number;
}

interface EvidenceSource {
    id: string;
    title: string;
    citation: string;
    method: string;
    url: string;
}

interface BrainConnection {
    from: string;
    to: string;
    label: string;
}

type VisualStreamKind = 'ventral' | 'dorsal' | 'salience';
type PathwayEmphasis = 'primary' | 'support' | 'candidate';

interface EarlyPathwayNode {
    label: string;
    detail: string;
}

interface VisualPathwayBranch {
    kind: VisualStreamKind;
    emphasis: PathwayEmphasis;
    badge: string;
    label: string;
    nodes: string[];
    description: string;
}

interface VisualPathwayProfile {
    title: string;
    channel: string;
    overview: string;
    note: string;
    branches: VisualPathwayBranch[];
    evidenceIds: string[];
}

type RecognitionStageId = 'features' | 'loc' | 'it' | 'memory';

interface RecognitionStage {
    id: RecognitionStageId;
    step: string;
    region: string;
    title: string;
    description: string;
}

interface StreamComparison {
    kind: 'ventral' | 'dorsal';
    label: string;
    question: string;
    route: string;
    output: string;
    awareness: string;
}

@Component({
    selector: 'wiz-page-dashboard',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.dashboard/view.scss */
@charset "UTF-8";
:host {
  display: block;
  height: 100%;
}

.brain-shell {
  color: #18181b;
}

.stimulus-option {
  min-width: 0;
  overflow: hidden;
  padding: 0;
}

.stimulus-option:focus-visible {
  outline: 3px solid rgba(14, 165, 233, 0.35);
  outline-offset: 2px;
}

.stimulus-thumb {
  position: relative;
  display: block;
  aspect-ratio: 16/9;
  overflow: hidden;
  background: #e4e4e7;
}

.stimulus-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 220ms ease, filter 220ms ease;
}

.stimulus-option:hover .stimulus-thumb img {
  transform: scale(1.035);
}

.stimulus-option[aria-pressed=true] .stimulus-thumb img {
  filter: saturate(1.04) contrast(1.03);
}

.stimulus-thumb-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(9, 9, 11, 0.04) 36%, rgba(9, 9, 11, 0.64) 100%);
  pointer-events: none;
}

.stimulus-category,
.stimulus-selected-mark {
  position: absolute;
  z-index: 2;
  top: 8px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(9, 9, 11, 0.55);
  color: #ffffff;
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  box-shadow: 0 4px 14px rgba(9, 9, 11, 0.16);
  backdrop-filter: blur(8px);
}

.stimulus-category {
  left: 8px;
  border-radius: 999px;
  padding: 5px 7px;
  letter-spacing: 0.08em;
}

.stimulus-selected-mark {
  right: 8px;
  display: grid;
  width: 22px;
  height: 22px;
  place-items: center;
  border-radius: 999px;
  background: #0ea5e9;
  font-size: 11px;
}

.stimulus-option-copy {
  display: block;
  padding: 10px 12px 11px;
}

.stimulus-option-copy strong,
.stimulus-option-copy small {
  display: block;
}

.stimulus-option-copy strong {
  overflow: hidden;
  font-size: 12px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stimulus-option-copy small {
  margin-top: 2px;
  font-size: 10px;
  opacity: 0.66;
}

.vision-flow-card {
  color: #ffffff;
}

.perception-stage {
  position: relative;
  min-height: 360px;
  overflow: hidden;
  isolation: isolate;
  background: radial-gradient(circle at 73% 46%, rgba(8, 145, 178, 0.15), transparent 24%), linear-gradient(115deg, #09090b 0%, #0b1220 43%, #081326 100%);
}

.perception-stage::before {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
  background-size: 28px 28px;
  content: "";
  mask-image: linear-gradient(90deg, #000000 0%, transparent 70%);
  pointer-events: none;
}

.stimulus-window {
  position: absolute;
  top: 50%;
  left: 24px;
  z-index: 5;
  width: 36%;
  max-width: 450px;
  height: auto;
  aspect-ratio: 8/5;
  overflow: hidden;
  transform: translateY(-50%);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 18px;
  background: #27272a;
  box-shadow: 0 26px 70px rgba(0, 0, 0, 0.44), 0 0 0 5px rgba(255, 255, 255, 0.035);
  cursor: crosshair;
  touch-action: manipulation;
}

.stimulus-window img,
.stimulus-window-shade {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.stimulus-window img {
  object-fit: cover;
}

.stimulus-window-shade {
  background: linear-gradient(180deg, transparent 38%, rgba(9, 9, 11, 0.9) 100%);
  pointer-events: none;
}

.stimulus-focus-marker {
  position: absolute;
  z-index: 7;
  display: grid;
  width: 27px;
  height: 27px;
  padding: 0;
  transform: translate(-50%, -50%);
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.86);
  border-radius: 999px;
  background: rgba(9, 9, 11, 0.62);
  color: #ffffff;
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
  box-shadow: 0 5px 18px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(6px);
  cursor: pointer;
  transition: transform 160ms ease, background 160ms ease, box-shadow 160ms ease;
}

.stimulus-focus-marker::after {
  position: absolute;
  inset: -7px;
  border: 1px solid transparent;
  border-radius: inherit;
  content: "";
  pointer-events: none;
}

.stimulus-focus-marker:hover {
  transform: translate(-50%, -50%) scale(1.1);
  background: rgba(8, 145, 178, 0.88);
}

.stimulus-focus-marker:focus-visible {
  outline: 3px solid rgba(103, 232, 249, 0.62);
  outline-offset: 4px;
}

.stimulus-focus-marker.is-selected {
  transform: translate(-50%, -50%) scale(1.12);
  border-color: #ffffff;
  background: #06b6d4;
  box-shadow: 0 0 0 4px rgba(6, 182, 212, 0.22), 0 0 28px rgba(34, 211, 238, 0.9);
}

.stimulus-focus-marker.is-selected::after {
  border-color: rgba(165, 243, 252, 0.8);
  animation: focus-marker-pulse 1.8s ease-out infinite;
}

.focus-delta {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  border-radius: 999px;
  padding: 3px 6px;
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
}

.focus-delta.is-up {
  background: rgba(16, 185, 129, 0.16);
  color: #a7f3d0;
}

.focus-delta.is-down {
  background: rgba(56, 189, 248, 0.16);
  color: #bae6fd;
}

.focus-delta.is-steady {
  background: rgba(161, 161, 170, 0.16);
  color: #d4d4d8;
}

.focus-delta-light.is-up,
.focus-change-inline.is-up {
  background: #ecfdf5;
  color: #047857;
}

.focus-delta-light.is-down,
.focus-change-inline.is-down {
  background: #eff6ff;
  color: #0369a1;
}

.focus-delta-light.is-steady,
.focus-change-inline.is-steady {
  background: #f4f4f5;
  color: #71717a;
}

.focus-change-inline {
  display: inline-block;
  margin: 0 2px;
  border-radius: 5px;
  padding: 1px 4px;
  font-size: 10px;
}

.focus-selection-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 5px 10px;
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #a5f3fc;
  border-radius: 12px;
  background: linear-gradient(135deg, #ecfeff 0%, #f0fdfa 100%);
}

.focus-selection-card div span,
.focus-selection-card div strong {
  display: block;
}

.focus-selection-card div span {
  color: #0891b2;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.focus-selection-card div strong {
  margin-top: 2px;
  color: #164e63;
  font-size: 13px;
}

.focus-selection-card > b {
  align-self: center;
  padding: 4px 7px;
  border-radius: 999px;
  background: #cffafe;
  color: #0e7490;
  font-size: 9px;
  white-space: nowrap;
}

.focus-selection-card p {
  grid-column: 1/-1;
  margin: 0;
  color: #155e75;
  font-size: 11px;
  line-height: 1.55;
}

.light-ray {
  position: absolute;
  top: 47%;
  right: 25.65%;
  left: calc(36% + 24px);
  z-index: 4;
  height: 2px;
  background: linear-gradient(90deg, rgba(251, 191, 36, 0.84), rgba(103, 232, 249, 0.9));
  box-shadow: 0 0 10px rgba(34, 211, 238, 0.66), 0 0 24px rgba(251, 191, 36, 0.34);
  pointer-events: none;
}

.light-ray::after {
  position: absolute;
  top: 50%;
  right: -1px;
  width: 8px;
  height: 8px;
  transform: translateY(-50%) rotate(45deg);
  border-top: 2px solid #a5f3fc;
  border-right: 2px solid #a5f3fc;
  content: "";
}

.light-ray i {
  position: absolute;
  top: 50%;
  left: 0;
  width: 7px;
  height: 7px;
  transform: translate(-50%, -50%);
  border-radius: 999px;
  background: #fef3c7;
  box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.16), 0 0 13px rgba(251, 191, 36, 0.92);
  animation: signal-travel 2.5s linear infinite;
}

.light-ray i:nth-child(2) {
  animation-delay: -0.83s;
}

.light-ray i:nth-child(3) {
  animation-delay: -1.66s;
}

.anatomy-model {
  position: absolute;
  top: 50%;
  right: 0;
  bottom: auto;
  z-index: 2;
  width: 45%;
  max-width: 576px;
  aspect-ratio: 1280/801;
  margin: 0;
  transform: translateY(-50%);
}

.anatomy-model::before {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: linear-gradient(90deg, #0b1220 0%, transparent 13%, transparent 100%);
  content: "";
  pointer-events: none;
}

.anatomy-model > img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: right center;
}

.anatomy-model-note {
  position: absolute;
  top: 14px;
  right: 18px;
  z-index: 5;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 999px;
  background: rgba(9, 9, 11, 0.7);
  padding: 5px 8px;
  color: #a1a1aa;
  font-size: 8px;
  font-weight: 800;
  line-height: 1;
  backdrop-filter: blur(7px);
}

.anatomy-marker {
  position: absolute;
  z-index: 4;
  width: 14px;
  height: 14px;
  transform: translate(-50%, -50%);
  border: 2px solid #ffffff;
  border-radius: 999px;
  background: #06b6d4;
  box-shadow: 0 0 0 4px rgba(34, 211, 238, 0.18), 0 0 18px rgba(34, 211, 238, 0.72);
}

.anatomy-marker::after {
  position: absolute;
  inset: -7px;
  border: 1px solid rgba(165, 243, 252, 0.72);
  border-radius: 999px;
  content: "";
  animation: anatomy-pulse 2.2s ease-out infinite;
}

.anatomy-marker b {
  position: absolute;
  min-width: max-content;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 7px;
  background: rgba(9, 9, 11, 0.76);
  padding: 4px 6px;
  color: #ecfeff;
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(7px);
}

.marker-eye {
  top: 47%;
  left: 43%;
  background: #f59e0b;
  box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.16), 0 0 18px rgba(251, 191, 36, 0.74);
}

.marker-eye b {
  top: 18px;
  right: 5px;
}

.marker-retina {
  top: 47%;
  left: 49%;
  background: #f97316;
}

.marker-retina b {
  right: -12px;
  bottom: 20px;
}

.marker-nerve {
  top: 51%;
  left: 61%;
}

.marker-nerve b {
  top: 18px;
  left: 5px;
}

.pathway-brain-hotspot {
  position: absolute;
  display: grid;
  place-items: center;
  padding: 0;
  transform: translate(-50%, -50%);
  border: 0;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  touch-action: manipulation;
  transition: top 260ms ease, left 260ms ease, width 260ms ease, height 260ms ease, transform 160ms ease;
}

.pathway-brain-hotspot::before {
  position: absolute;
  inset: 1px;
  border-radius: inherit;
  background: currentColor;
  opacity: 0.5;
  filter: blur(6px);
  content: "";
  animation: pathway-hotspot-pulse 2.3s ease-in-out infinite;
}

.pathway-brain-hotspot::after {
  position: absolute;
  inset: 7px;
  border: 1px solid currentColor;
  border-radius: inherit;
  opacity: 0.88;
  content: "";
}

.pathway-hotspot-core {
  position: relative;
  z-index: 2;
  display: grid;
  width: 32px;
  height: 32px;
  place-content: center;
  border: 2px solid rgba(255, 255, 255, 0.92);
  border-radius: 999px;
  background: rgba(9, 9, 11, 0.82);
  box-shadow: 0 0 18px currentColor;
  color: #ffffff;
  text-align: center;
  backdrop-filter: blur(6px);
  transition: border-width 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.pathway-hotspot-core strong,
.pathway-hotspot-core small {
  display: block;
  line-height: 1;
}

.pathway-hotspot-core strong {
  font-size: 8px;
  font-weight: 900;
}

.pathway-hotspot-core small {
  margin-top: 2px;
  color: #f4f4f5;
  font-size: 7px;
  font-weight: 800;
}

.pathway-brain-hotspot:hover,
.pathway-brain-hotspot:focus-visible {
  transform: translate(-50%, -50%) scale(1.08);
}

.pathway-brain-hotspot:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

.pathway-brain-hotspot.is-selected .pathway-hotspot-core {
  border-width: 3px;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2), 0 0 26px currentColor;
  transform: scale(1.08);
}

.pathway-map {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: radial-gradient(circle at 50% 0%, rgba(14, 116, 144, 0.14), transparent 36%), #09090b;
  padding: 18px;
}

.pathway-map-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}

.pathway-map-head h3 {
  margin-top: 4px;
  color: #fafafa;
  font-size: 13px;
  font-weight: 850;
}

.pathway-channel {
  display: flex;
  max-width: 520px;
  align-items: center;
  gap: 9px;
  border: 1px solid rgba(103, 232, 249, 0.16);
  border-radius: 999px;
  background: rgba(8, 145, 178, 0.08);
  padding: 7px 10px;
}

.pathway-channel span {
  flex: 0 0 auto;
  color: #67e8f9;
  font-size: 8px;
  font-weight: 900;
  letter-spacing: 0.06em;
}

.pathway-channel strong {
  min-width: 0;
  color: #d4d4d8;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.35;
}

.common-pathway {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
}

.common-pathway-node {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 76px;
  align-items: flex-start;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.035);
  padding: 10px;
}

.common-pathway-node:not(:last-child)::after {
  position: absolute;
  top: 50%;
  right: -9px;
  z-index: 2;
  transform: translate(50%, -50%);
  color: #67e8f9;
  content: "→";
  font-size: 12px;
  font-weight: 900;
}

.common-pathway-index {
  display: grid;
  width: 21px;
  height: 21px;
  flex: 0 0 21px;
  place-items: center;
  border: 1px solid rgba(103, 232, 249, 0.25);
  border-radius: 7px;
  background: rgba(8, 145, 178, 0.12);
  color: #67e8f9;
  font-size: 8px;
  font-weight: 900;
}

.common-pathway-copy,
.common-pathway-copy strong,
.common-pathway-copy small {
  display: block;
  min-width: 0;
}

.common-pathway-copy strong {
  color: #f4f4f5;
  font-size: 10px;
  font-weight: 850;
}

.common-pathway-copy small {
  margin-top: 4px;
  color: #a1a1aa;
  font-size: 9px;
  font-weight: 650;
  line-height: 1.45;
}

.stream-split {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  margin: 14px 0 10px;
  border-left: 3px solid #67e8f9;
  border-radius: 0 9px 9px 0;
  background: rgba(255, 255, 255, 0.035);
  padding: 9px 11px;
}

.stream-split span {
  color: #ecfeff;
  font-size: 9px;
  font-weight: 900;
}

.stream-split small {
  color: #a1a1aa;
  font-size: 9px;
  font-weight: 650;
  line-height: 1.5;
}

.stream-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.stream-grid.has-three-branches {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.stream-branch {
  --stream-color: #a1a1aa;
  --stream-soft: rgba(161, 161, 170, 0.1);
  --stream-border: rgba(161, 161, 170, 0.34);
  min-width: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: 2px solid var(--stream-color);
  border-radius: 12px;
  background: linear-gradient(145deg, var(--stream-soft), rgba(255, 255, 255, 0.025));
  padding: 12px;
}

.stream-branch[data-kind=ventral] {
  --stream-color: #c084fc;
  --stream-soft: rgba(168, 85, 247, 0.11);
  --stream-border: rgba(192, 132, 252, 0.34);
}

.stream-branch[data-kind=dorsal] {
  --stream-color: #22d3ee;
  --stream-soft: rgba(6, 182, 212, 0.1);
  --stream-border: rgba(34, 211, 238, 0.34);
}

.stream-branch[data-kind=salience] {
  --stream-color: #fb7185;
  --stream-soft: rgba(244, 63, 94, 0.1);
  --stream-border: rgba(251, 113, 133, 0.34);
}

.stream-branch.is-primary {
  border-right-color: var(--stream-border);
  border-bottom-color: var(--stream-border);
  border-left-color: var(--stream-border);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.14);
}

.stream-branch.is-candidate {
  border-style: dashed;
  border-top-style: dashed;
}

.stream-branch header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stream-branch-badge {
  flex: 0 0 auto;
  border: 1px solid var(--stream-color);
  border-radius: 999px;
  background: var(--stream-soft);
  padding: 4px 6px;
  color: var(--stream-color);
  font-size: 8px;
  font-weight: 900;
  letter-spacing: 0.04em;
  line-height: 1;
}

.stream-branch h4 {
  min-width: 0;
  color: #f4f4f5;
  font-size: 10px;
  font-weight: 850;
  line-height: 1.35;
}

.stream-route {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  overflow-x: auto;
  padding: 1px 0 4px;
  scrollbar-width: thin;
}

.stream-route span {
  flex: 0 0 auto;
  border: 1px solid var(--stream-border);
  border-radius: 7px;
  background: rgba(9, 9, 11, 0.48);
  padding: 5px 7px;
  color: #e4e4e7;
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
}

.stream-route i {
  flex: 0 0 auto;
  color: var(--stream-color);
  font-size: 9px;
  font-style: normal;
  font-weight: 900;
}

.stream-branch > p {
  margin-top: 7px;
  color: #a1a1aa;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.5;
}

.object-processing-panel {
  margin-top: 14px;
  border: 1px solid rgba(192, 132, 252, 0.2);
  border-radius: 16px;
  background: radial-gradient(circle at 8% 0%, rgba(168, 85, 247, 0.12), transparent 28%), radial-gradient(circle at 92% 0%, rgba(6, 182, 212, 0.1), transparent 26%), rgba(24, 24, 27, 0.76);
  padding: 14px;
}

.object-processing-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.object-processing-head > div:first-child {
  min-width: 0;
}

.object-processing-kicker {
  color: #c4b5fd;
  font-size: 8px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.object-processing-head h4 {
  margin-top: 5px;
  color: #fafafa;
  font-size: 13px;
  font-weight: 850;
  line-height: 1.35;
}

.object-processing-head > div:first-child > p:last-child {
  max-width: 760px;
  margin-top: 5px;
  color: #a1a1aa;
  font-size: 9px;
  font-weight: 650;
  line-height: 1.55;
}

.focus-processing-snapshot {
  display: grid;
  min-width: 230px;
  flex: 0 0 auto;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 2px 10px;
  border: 1px solid rgba(103, 232, 249, 0.2);
  border-radius: 11px;
  background: rgba(8, 145, 178, 0.08);
  padding: 9px 10px;
}

.focus-processing-snapshot span {
  color: #67e8f9;
  font-size: 8px;
  font-weight: 900;
}

.focus-processing-snapshot strong {
  grid-column: 1;
  color: #ecfeff;
  font-size: 11px;
  font-weight: 850;
}

.focus-processing-snapshot small {
  grid-row: 1/span 2;
  grid-column: 2;
  align-self: center;
  color: #d4d4d8;
  font-size: 8px;
  font-weight: 750;
  white-space: nowrap;
}

.focus-processing-snapshot em {
  grid-column: 1/-1;
  margin-top: 4px;
  color: #71717a;
  font-size: 7px;
  font-style: normal;
  font-weight: 650;
  line-height: 1.4;
}

.recognition-axis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 13px;
}

.recognition-stage {
  position: relative;
  min-width: 0;
  border: 1px solid rgba(192, 132, 252, 0.2);
  border-top: 2px solid rgba(192, 132, 252, 0.72);
  border-radius: 12px;
  background: linear-gradient(145deg, rgba(126, 34, 206, 0.11), rgba(9, 9, 11, 0.5));
  padding: 11px;
}

.recognition-stage-top {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 7px;
}

.recognition-stage-number {
  display: grid;
  width: 23px;
  height: 23px;
  flex: 0 0 23px;
  place-items: center;
  border: 1px solid rgba(216, 180, 254, 0.36);
  border-radius: 7px;
  background: rgba(168, 85, 247, 0.14);
  color: #d8b4fe;
  font-size: 8px;
  font-weight: 900;
}

.recognition-stage-region {
  min-width: 0;
  overflow: hidden;
  color: #c4b5fd;
  font-size: 8px;
  font-weight: 850;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recognition-stage h5 {
  margin-top: 9px;
  color: #f4f4f5;
  font-size: 10px;
  font-weight: 850;
  line-height: 1.35;
}

.recognition-stage > p {
  margin-top: 5px;
  color: #a1a1aa;
  font-size: 8px;
  font-weight: 620;
  line-height: 1.55;
}

.recognition-focus-detail {
  margin-top: 9px;
  border-left: 2px solid rgba(103, 232, 249, 0.72);
  border-radius: 0 7px 7px 0;
  background: rgba(8, 145, 178, 0.08);
  padding: 7px 8px;
}

.recognition-focus-detail span {
  display: block;
  color: #67e8f9;
  font-size: 7px;
  font-weight: 900;
}

.recognition-focus-detail p {
  margin-top: 3px;
  color: #d4d4d8;
  font-size: 8px;
  font-weight: 650;
  line-height: 1.45;
}

.recognition-stage-arrow {
  position: absolute;
  top: 50%;
  right: -10px;
  z-index: 3;
  display: grid;
  width: 10px;
  height: 20px;
  transform: translate(50%, -50%);
  place-items: center;
  background: #18181b;
  color: #d8b4fe;
  font-size: 10px;
  font-style: normal;
  font-weight: 900;
}

.stream-comparison-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.stream-comparison-card {
  --compare-color: #a1a1aa;
  --compare-soft: rgba(161, 161, 170, 0.08);
  --compare-border: rgba(161, 161, 170, 0.24);
  min-width: 0;
  border: 1px solid var(--compare-border);
  border-radius: 12px;
  background: var(--compare-soft);
  padding: 11px;
}

.stream-comparison-card[data-kind=ventral] {
  --compare-color: #d8b4fe;
  --compare-soft: rgba(168, 85, 247, 0.08);
  --compare-border: rgba(192, 132, 252, 0.24);
}

.stream-comparison-card[data-kind=dorsal] {
  --compare-color: #67e8f9;
  --compare-soft: rgba(6, 182, 212, 0.07);
  --compare-border: rgba(34, 211, 238, 0.22);
}

.stream-comparison-card header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.stream-comparison-card header span {
  color: var(--compare-color);
  font-size: 8px;
  font-weight: 900;
}

.stream-comparison-card header strong {
  color: #fafafa;
  font-size: 10px;
  font-weight: 850;
  text-align: right;
}

.stream-comparison-card dl {
  display: grid;
  gap: 7px;
  margin-top: 9px;
}

.stream-comparison-card dl > div {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  gap: 8px;
}

.stream-comparison-card dt {
  color: #71717a;
  font-size: 7px;
  font-weight: 900;
}

.stream-comparison-card dd {
  min-width: 0;
  color: #d4d4d8;
  font-size: 8px;
  font-weight: 650;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.gaze-processing-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(250px, 0.7fr);
  gap: 10px;
  margin-top: 10px;
}

.gaze-sequence-card,
.focus-impact-card {
  min-width: 0;
  border: 1px solid rgba(103, 232, 249, 0.15);
  border-radius: 12px;
  background: rgba(9, 9, 11, 0.42);
  padding: 11px;
}

.gaze-sequence-card header,
.focus-impact-card header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.gaze-sequence-card header span,
.focus-impact-card header span {
  color: #67e8f9;
  font-size: 8px;
  font-weight: 900;
}

.gaze-sequence-card header strong,
.focus-impact-card header strong {
  color: #f4f4f5;
  font-size: 9px;
  font-weight: 820;
  text-align: right;
}

.gaze-sequence {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
  margin: 9px 0 0;
  padding: 0;
  list-style: none;
}

.gaze-sequence li {
  min-width: 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.025);
  padding: 8px;
}

.gaze-sequence b {
  display: grid;
  width: 18px;
  height: 18px;
  place-items: center;
  border-radius: 6px;
  background: rgba(6, 182, 212, 0.12);
  color: #67e8f9;
  font-size: 7px;
  font-weight: 900;
}

.gaze-sequence span {
  display: block;
  margin-top: 6px;
  color: #a1a1aa;
  font-size: 8px;
  font-weight: 650;
  line-height: 1.45;
}

.gaze-current-action {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 8px;
  margin-top: 9px;
  border-left: 2px solid #22d3ee;
  border-radius: 0 8px 8px 0;
  background: rgba(6, 182, 212, 0.07);
  padding: 8px 9px;
}

.gaze-current-action span {
  color: #67e8f9;
  font-size: 7px;
  font-weight: 900;
}

.gaze-current-action p {
  color: #d4d4d8;
  font-size: 8px;
  font-weight: 650;
  line-height: 1.45;
}

.gaze-sequence-card > small {
  display: block;
  margin-top: 7px;
  color: #71717a;
  font-size: 7px;
  font-weight: 650;
  line-height: 1.45;
}

.focus-impact-list {
  display: grid;
  gap: 6px;
  margin-top: 9px;
}

.focus-impact-row {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.025);
  padding: 7px 8px;
  color: inherit;
  text-align: left;
  transition: border-color 150ms ease, background 150ms ease;
}

.focus-impact-row:hover {
  border-color: rgba(103, 232, 249, 0.28);
  background: rgba(6, 182, 212, 0.06);
}

.focus-impact-row:focus-visible {
  outline: 2px solid #67e8f9;
  outline-offset: 2px;
}

.focus-impact-row > span {
  min-width: 0;
}

.focus-impact-row strong,
.focus-impact-row small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.focus-impact-row strong {
  color: #e4e4e7;
  font-size: 8px;
  font-weight: 850;
}

.focus-impact-row small {
  margin-top: 2px;
  color: #71717a;
  font-size: 7px;
  font-weight: 650;
}

.focus-impact-row > b {
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 4px 6px;
  font-size: 8px;
  font-weight: 900;
}

.focus-impact-row > b.is-up {
  background: rgba(244, 63, 94, 0.12);
  color: #fda4af;
}

.focus-impact-row > b.is-down {
  background: rgba(14, 165, 233, 0.12);
  color: #7dd3fc;
}

.focus-impact-row > b.is-steady {
  background: rgba(161, 161, 170, 0.1);
  color: #a1a1aa;
}

.focus-impact-card > p {
  margin-top: 8px;
  color: #71717a;
  font-size: 7px;
  font-weight: 620;
  line-height: 1.45;
}

.stream-awareness-note {
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr);
  gap: 5px 12px;
  margin-top: 10px;
  border: 1px solid rgba(251, 191, 36, 0.16);
  border-radius: 11px;
  background: rgba(245, 158, 11, 0.055);
  padding: 10px 11px;
}

.stream-awareness-note span {
  color: #fcd34d;
  font-size: 8px;
  font-weight: 900;
}

.stream-awareness-note p {
  color: #e4e4e7;
  font-size: 8px;
  font-weight: 680;
  line-height: 1.5;
}

.stream-awareness-note small {
  grid-column: 2;
  color: #71717a;
  font-size: 7px;
  font-weight: 620;
  line-height: 1.45;
}

.object-processing-evidence {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 7px;
  margin-top: 10px;
}

.object-processing-evidence > span {
  grid-column: 1/-1;
  color: #71717a;
  font-size: 7px;
  font-weight: 900;
  letter-spacing: 0.05em;
}

.object-processing-evidence a {
  min-width: 0;
  border: 1px solid rgba(125, 211, 252, 0.12);
  border-radius: 8px;
  background: rgba(14, 165, 233, 0.045);
  padding: 7px;
  text-decoration: none;
}

.object-processing-evidence a:hover {
  border-color: rgba(125, 211, 252, 0.28);
  background: rgba(14, 165, 233, 0.08);
}

.object-processing-evidence strong,
.object-processing-evidence small {
  display: block;
}

.object-processing-evidence strong {
  color: #7dd3fc;
  font-size: 7px;
  font-weight: 850;
  line-height: 1.35;
}

.object-processing-evidence small {
  margin-top: 4px;
  color: #71717a;
  font-size: 7px;
  font-weight: 620;
  line-height: 1.4;
}

.pathway-map-footer {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(260px, 0.65fr);
  gap: 14px;
  margin-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 12px;
}

.pathway-caution strong,
.pathway-evidence > span {
  display: block;
  color: #71717a;
  font-size: 8px;
  font-weight: 900;
  letter-spacing: 0.05em;
}

.pathway-caution p {
  margin-top: 4px;
  color: #d4d4d8;
  font-size: 9px;
  font-weight: 650;
  line-height: 1.5;
}

.pathway-caution small {
  display: block;
  margin-top: 4px;
  color: #71717a;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.45;
}

.pathway-evidence {
  min-width: 0;
}

.pathway-evidence a {
  display: block;
  overflow: hidden;
  margin-top: 4px;
  color: #7dd3fc;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pathway-evidence a:hover {
  color: #cffafe;
  text-decoration: underline;
}

@keyframes focus-marker-pulse {
  0% {
    transform: scale(0.72);
    opacity: 0.9;
  }
  100% {
    transform: scale(1.55);
    opacity: 0;
  }
}
@keyframes signal-travel {
  0% {
    left: 0;
    opacity: 0;
  }
  12%, 82% {
    opacity: 1;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
}
@keyframes anatomy-pulse {
  0% {
    transform: scale(0.72);
    opacity: 0.9;
  }
  100% {
    transform: scale(1.45);
    opacity: 0;
  }
}
@keyframes pathway-hotspot-pulse {
  0%, 100% {
    transform: scale(0.8);
    opacity: 0.34;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.66;
  }
}
.surface-stage {
  min-height: 520px;
  background: radial-gradient(circle at 50% 12%, rgba(219, 234, 254, 0.72), transparent 38%), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.surface-views {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  min-height: 462px;
  padding: 58px 18px 74px;
  background: #e4e4e7;
}

.surface-views-single {
  grid-template-columns: minmax(0, 620px);
  justify-content: center;
}

.surface-view {
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.98);
}

.surface-caption {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px 0;
  color: #18181b;
}

.surface-caption strong,
.surface-caption small {
  display: block;
}

.surface-caption strong {
  font-size: 12px;
  font-weight: 800;
}

.surface-caption small,
.surface-caption-value {
  margin-top: 2px;
  color: #a1a1aa;
  font-size: 10px;
  font-weight: 600;
}

.surface-frame {
  position: relative;
  flex: 1;
  min-height: 320px;
  overflow: hidden;
  isolation: isolate;
}

.surface-base,
.surface-heat {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.surface-base {
  z-index: 1;
  object-fit: contain;
  object-position: center;
  filter: saturate(0.72) contrast(1.14) brightness(0.98);
  pointer-events: none;
  user-select: none;
}

.surface-heat {
  z-index: 2;
  background-repeat: no-repeat;
  opacity: 0.78;
  mix-blend-mode: multiply;
  animation: heat-breathe 3.8s ease-in-out infinite;
  transition: background 260ms ease;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
}

.surface-heat-lateral {
  -webkit-mask-image: url("/assets/brain/brain-lateral-v2.png");
  mask-image: url("/assets/brain/brain-lateral-v2.png");
}

.surface-heat-dorsal {
  -webkit-mask-image: url("/assets/brain/brain-dorsal-v2.png");
  mask-image: url("/assets/brain/brain-dorsal-v2.png");
}

@supports (mix-blend-mode: color) {
  .surface-heat {
    opacity: 0.94;
    mix-blend-mode: color;
  }
}
.surface-hotspot {
  position: absolute;
  z-index: 5;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 0;
  background: transparent;
  transform: translate(-50%, -50%);
  cursor: pointer;
}

.surface-hotspot-ring {
  position: absolute;
  inset: 4px;
  border: 1px solid rgba(255, 255, 255, 0);
  border-radius: 999px;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.surface-hotspot:hover .surface-hotspot-ring,
.surface-hotspot:focus .surface-hotspot-ring {
  border-color: rgba(255, 255, 255, 0.92);
  box-shadow: 0 0 0 2px rgba(24, 24, 27, 0.76), 0 0 18px rgba(255, 255, 255, 0.9);
}

.surface-hotspot.is-selected .surface-hotspot-ring {
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 2px #18181b, 0 0 0 6px rgba(255, 255, 255, 0.44), 0 0 24px rgba(255, 255, 255, 0.95);
  animation: selected-ring 1.8s ease-in-out infinite;
}

.surface-hotspot-label {
  position: absolute;
  left: 50%;
  top: calc(100% + 6px);
  min-width: 54px;
  transform: translateX(-50%);
  border: 1px solid rgba(255, 255, 255, 0.26);
  border-radius: 8px;
  background: rgba(24, 24, 27, 0.88);
  padding: 5px 7px;
  color: #ffffff;
  text-align: center;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.22);
  backdrop-filter: blur(8px);
}

.surface-hotspot-label strong,
.surface-hotspot-label small {
  display: block;
  line-height: 1.2;
}

.surface-hotspot-label strong {
  font-size: 10px;
  font-weight: 900;
}

.surface-hotspot-label small {
  margin-top: 2px;
  color: #bae6fd;
  font-size: 9px;
  font-weight: 800;
}

.surface-legend {
  position: absolute;
  right: 16px;
  bottom: 14px;
  left: 16px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 18px;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.94);
  padding: 10px 12px;
  color: #52525b;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(10px);
}

.surface-legend-copy {
  min-width: 0;
}

.surface-legend-copy strong,
.surface-legend-copy span {
  display: block;
}

.surface-legend-copy strong {
  color: #18181b;
  font-size: 11px;
  font-weight: 800;
}

.surface-legend-copy span,
.surface-legend-note,
.surface-scale {
  color: #71717a;
  font-size: 10px;
  font-weight: 600;
}

.surface-scale {
  display: flex;
  min-width: 190px;
  align-items: center;
  gap: 8px;
}

.surface-scale i {
  display: block;
  height: 9px;
  flex: 1;
  border: 1px solid rgba(24, 24, 27, 0.08);
  border-radius: 999px;
  background: linear-gradient(90deg, #1d4ed8 0%, #14b8a6 30%, #ef4444 68%, #fde047 100%);
}

.surface-legend-note {
  margin-left: auto;
  white-space: nowrap;
}

@keyframes heat-breathe {
  0%, 100% {
    filter: saturate(1.04);
  }
  50% {
    filter: saturate(1.2);
  }
}
@keyframes selected-ring {
  0%, 100% {
    transform: scale(0.92);
  }
  50% {
    transform: scale(1.08);
  }
}
@media (max-width: 900px) {
  .pathway-map-head {
    align-items: flex-start;
    flex-direction: column;
  }
  .pathway-channel {
    max-width: none;
  }
  .common-pathway {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .common-pathway-node:nth-child(4)::after {
    content: none;
  }
  .stream-grid.has-three-branches {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .stream-grid.has-three-branches .stream-branch:last-child {
    grid-column: 1/-1;
  }
  .recognition-axis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .recognition-stage:nth-child(2) .recognition-stage-arrow {
    display: none;
  }
  .gaze-processing-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .object-processing-evidence {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .pathway-map-footer {
    grid-template-columns: minmax(0, 1fr);
  }
  .surface-views {
    grid-template-columns: 1fr;
    padding-bottom: 82px;
  }
  .surface-frame {
    min-height: 380px;
  }
  .surface-legend {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px 14px;
  }
  .surface-legend-note {
    margin-left: 0;
  }
}
@media (max-width: 760px) {
  .perception-stage {
    min-height: clamp(590px, 92vw, 700px);
  }
  .stimulus-window {
    top: 16px;
    right: auto;
    left: 50%;
    width: calc(100% - 32px);
    max-width: 520px;
    height: auto;
    transform: translateX(-50%);
  }
  .anatomy-model {
    top: clamp(252px, 62.5vw + 8px, 346px);
    bottom: auto;
    width: 100%;
    max-width: 550px;
    height: auto;
    transform: none;
  }
  .light-ray {
    display: none;
  }
}
@media (max-width: 640px) {
  .pathway-map {
    padding: 14px;
  }
  .pathway-channel {
    align-items: flex-start;
    flex-direction: column;
    border-radius: 12px;
  }
  .common-pathway {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
  .common-pathway-node {
    min-height: auto;
  }
  .common-pathway-node:not(:last-child)::after,
  .common-pathway-node:nth-child(4)::after {
    top: auto;
    right: 50%;
    bottom: -15px;
    transform: translate(50%, 50%);
    content: "↓";
  }
  .stream-split {
    grid-template-columns: minmax(0, 1fr);
    gap: 5px;
  }
  .stream-grid,
  .stream-grid.has-three-branches {
    grid-template-columns: minmax(0, 1fr);
  }
  .stream-grid.has-three-branches .stream-branch:last-child {
    grid-column: auto;
  }
  .object-processing-head {
    flex-direction: column;
  }
  .focus-processing-snapshot {
    width: 100%;
    min-width: 0;
  }
  .recognition-axis,
  .stream-comparison-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .recognition-stage:nth-child(2) .recognition-stage-arrow {
    display: grid;
  }
  .recognition-stage-arrow {
    top: auto;
    right: 50%;
    bottom: -10px;
    width: 20px;
    height: 10px;
    transform: translate(50%, 50%);
  }
  .recognition-stage-arrow {
    font-size: 0;
  }
  .recognition-stage-arrow::after {
    color: #d8b4fe;
    content: "↓";
    font-size: 10px;
  }
  .gaze-sequence {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .gaze-current-action,
  .stream-awareness-note {
    grid-template-columns: minmax(0, 1fr);
  }
  .stream-awareness-note small {
    grid-column: 1;
  }
  .object-processing-evidence {
    grid-template-columns: minmax(0, 1fr);
  }
  .surface-stage {
    min-height: 500px;
  }
  .surface-views {
    padding: 58px 8px 96px;
  }
  .surface-frame {
    min-height: 320px;
  }
  .surface-scale {
    min-width: 150px;
  }
  .surface-legend-copy span {
    display: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .light-ray i,
  .anatomy-marker::after,
  .pathway-brain-hotspot::before,
  .stimulus-focus-marker.is-selected::after,
  .surface-heat,
  .surface-hotspot-ring {
    animation: none;
  }
  .stimulus-thumb img,
  .stimulus-focus-marker,
  .surface-hotspot-ring {
    transition: none;
  }
}
/* Visual-first object, gaze and action processing map */
.perception-stage {
  min-height: 0;
  overflow: hidden;
  background: radial-gradient(circle at 72% 48%, rgba(109, 40, 217, 0.14), transparent 34%), radial-gradient(circle at 66% 18%, rgba(8, 145, 178, 0.13), transparent 30%), linear-gradient(135deg, #07070a 0%, #0b1220 52%, #09090b 100%);
}

.object-flow-visual {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 0;
}

.processing-canvas {
  position: relative;
  width: 100%;
  height: clamp(450px, 39vw, 520px);
  min-height: 450px;
  overflow: hidden;
  isolation: isolate;
}

.processing-canvas::before {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 32px 32px;
  content: "";
  mask-image: radial-gradient(circle at 67% 50%, #000000 18%, transparent 74%);
  pointer-events: none;
}

.processing-brain-image {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  opacity: 0.72;
  filter: saturate(0.82) contrast(1.08) drop-shadow(0 24px 50px rgba(0, 0, 0, 0.52));
  object-fit: contain;
  object-position: right center;
  pointer-events: none;
  user-select: none;
}

.processing-anatomy-note {
  position: absolute;
  top: 12px;
  right: 14px;
  z-index: 5;
  border: 1px solid rgba(125, 211, 252, 0.24);
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.72);
  padding: 6px 9px;
  color: #bae6fd;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.02em;
  backdrop-filter: blur(8px);
}

.processing-network {
  position: absolute;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

.processing-wire {
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}

.processing-wire-input {
  stroke: #fbbf24;
  stroke-width: 7px;
  stroke-dasharray: 15 14;
  filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.76));
  animation: processing-wire-flow 1.15s linear infinite;
}

.processing-wire-dorsal {
  stroke: #22d3ee;
  filter: drop-shadow(0 0 9px rgba(34, 211, 238, 0.82));
  animation: processing-route-breathe 2.4s ease-in-out infinite;
}

.processing-wire-ventral {
  stroke: #c084fc;
  filter: drop-shadow(0 0 9px rgba(192, 132, 252, 0.82));
  animation: processing-route-breathe 2.4s ease-in-out -1.2s infinite;
}

.processing-wire-feedback {
  stroke: #f0abfc;
  stroke-width: 3px;
  stroke-dasharray: 8 10;
  opacity: 0.72;
  filter: drop-shadow(0 0 5px rgba(240, 171, 252, 0.55));
  animation: processing-feedback-flow 2.2s linear infinite;
}

.processing-wire-salience {
  stroke: #fb7185;
  stroke-dasharray: 4 11;
  filter: drop-shadow(0 0 8px rgba(251, 113, 133, 0.78));
  animation: processing-feedback-flow 1.55s linear infinite;
}

.processing-stimulus-window {
  top: 51%;
  left: 2.2%;
  width: 19.5%;
  max-width: 250px;
  transform: translateY(-50%);
  border-width: 2px;
  border-color: rgba(253, 224, 71, 0.72);
  border-radius: 16px;
  box-shadow: 0 22px 52px rgba(0, 0, 0, 0.46), 0 0 0 5px rgba(251, 191, 36, 0.08), 0 0 34px rgba(251, 191, 36, 0.16);
}

.processing-stimulus-window .stimulus-focus-marker {
  width: 38px;
  height: 38px;
  font-size: 12px;
}

.processing-stimulus-window .stimulus-focus-marker::after {
  inset: -5px;
}

.processing-stimulus-caption {
  position: absolute;
  right: 10px;
  bottom: 9px;
  left: 10px;
  z-index: 6;
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  pointer-events: none;
}

.processing-stimulus-caption small,
.processing-stimulus-caption strong {
  min-width: 0;
}

.processing-stimulus-caption small {
  color: #fde68a;
  font-size: 11px;
  font-weight: 850;
}

.processing-stimulus-caption strong {
  overflow: hidden;
  color: #ffffff;
  font-size: 14px;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.processing-focus-lens {
  position: absolute;
  top: 37%;
  left: 23.5%;
  z-index: 6;
  width: 76px;
  height: 76px;
  transform: translate(-10%, -50%);
  border: 3px solid #fde047;
  border-radius: 999px;
  background-repeat: no-repeat;
  background-size: 340% auto;
  box-shadow: 0 0 0 7px rgba(250, 204, 21, 0.1), 0 0 32px rgba(250, 204, 21, 0.58);
  pointer-events: none;
}

.processing-focus-lens::after {
  position: absolute;
  inset: -10px;
  border: 1px dashed rgba(253, 224, 71, 0.62);
  border-radius: inherit;
  content: "";
  animation: processing-focus-spin 9s linear infinite;
}

.processing-focus-lens i,
.processing-focus-lens span {
  position: absolute;
  top: 50%;
  left: 50%;
  background: rgba(255, 255, 255, 0.92);
  content: "";
}

.processing-focus-lens i {
  width: 26px;
  height: 1px;
  transform: translate(-50%, -50%);
}

.processing-focus-lens span {
  width: 1px;
  height: 26px;
  transform: translate(-50%, -50%);
}

.processing-gaze-link {
  position: absolute;
  top: 51%;
  left: 28%;
  z-index: 4;
  width: 34.5%;
  height: 2px;
  transform: translateY(-50%);
  background: linear-gradient(90deg, #fde047, #fbbf24);
  box-shadow: 0 0 10px rgba(251, 191, 36, 0.72);
  pointer-events: none;
}

.processing-gaze-link i {
  position: absolute;
  top: 50%;
  left: 0;
  width: 7px;
  height: 7px;
  transform: translate(-50%, -50%);
  border-radius: 999px;
  background: #fef3c7;
  box-shadow: 0 0 12px rgba(251, 191, 36, 0.92);
  animation: processing-gaze-dot 1.35s linear infinite;
}

.processing-gaze-link i:nth-child(2) {
  animation-delay: -0.45s;
}

.processing-gaze-link i:nth-child(3) {
  animation-delay: -0.9s;
}

.processing-node {
  --node-color: #fbbf24;
  --node-soft: rgba(251, 191, 36, 0.16);
  position: absolute;
  z-index: 7;
  display: grid;
  width: 108px;
  min-width: 0;
  justify-items: center;
  padding: 0;
  transform: translate(-50%, -50%);
  border: 0;
  background: transparent;
  color: #ffffff;
  text-align: center;
  cursor: default;
  touch-action: manipulation;
}

button.processing-node {
  appearance: none;
  cursor: pointer;
}

.processing-node.is-input {
  --node-color: #fbbf24;
  --node-soft: rgba(251, 191, 36, 0.16);
}

.processing-node.is-dorsal {
  --node-color: #22d3ee;
  --node-soft: rgba(34, 211, 238, 0.16);
}

.processing-node.is-ventral {
  --node-color: #c084fc;
  --node-soft: rgba(192, 132, 252, 0.17);
}

.processing-node.is-salience {
  --node-color: #fb7185;
  --node-soft: rgba(251, 113, 133, 0.17);
}

.processing-node-orb {
  position: relative;
  display: grid;
  width: 50px;
  height: 50px;
  place-content: center;
  border: 2px solid rgba(255, 255, 255, 0.88);
  border-radius: 999px;
  background: radial-gradient(circle, rgba(9, 9, 11, 0.94) 0 55%, transparent 57%), conic-gradient(var(--node-color) var(--node-angle), rgba(63, 63, 70, 0.62) 0);
  box-shadow: 0 0 0 5px var(--node-soft), 0 0 var(--node-glow) color-mix(in srgb, var(--node-color) 78%, transparent);
  color: #ffffff;
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.processing-node-orb svg {
  width: 27px;
  height: 27px;
  color: var(--node-color);
}

.processing-node-orb b,
.processing-node-orb em {
  display: block;
  line-height: 1;
}

.processing-node-orb b {
  color: #ffffff;
  font-size: 13px;
  font-weight: 950;
  letter-spacing: -0.02em;
}

.processing-node-orb em {
  margin-top: 4px;
  color: var(--node-color);
  font-size: 10px;
  font-style: normal;
  font-weight: 900;
}

.processing-node-copy {
  display: grid;
  min-width: max-content;
  margin-top: 8px;
  gap: 2px;
  border: 1px solid color-mix(in srgb, var(--node-color) 38%, transparent);
  border-radius: 9px;
  background: rgba(9, 9, 11, 0.82);
  padding: 5px 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(8px);
}

.processing-node-copy strong {
  color: #ffffff;
  font-size: 14px;
  font-weight: 900;
  line-height: 1.15;
}

.processing-node-copy small {
  color: #d4d4d8;
  font-size: 11px;
  font-weight: 750;
  line-height: 1.2;
}

button.processing-node:hover .processing-node-orb,
button.processing-node:focus-visible .processing-node-orb,
.processing-node.is-selected .processing-node-orb {
  transform: scale(1.1);
  box-shadow: 0 0 0 7px var(--node-soft), 0 0 calc(var(--node-glow) + 10px) var(--node-color);
}

button.processing-node:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: 6px;
  border-radius: 14px;
}

.processing-output-node {
  width: 122px;
}

.processing-output-node .processing-node-orb {
  width: 64px;
  height: 64px;
  border-width: 3px;
}

.processing-output-node .processing-node-orb svg {
  width: 34px;
  height: 34px;
}

.processing-output-node .processing-node-copy strong {
  font-size: 15px;
}

.node-eye .processing-node-orb {
  width: 48px;
  height: 48px;
  border: 3px solid #fbbf24;
  background: rgba(9, 9, 11, 0.16);
  box-shadow: 0 0 0 6px rgba(251, 191, 36, 0.12), 0 0 26px rgba(251, 191, 36, 0.82);
}

.node-eye .processing-node-orb svg {
  width: 23px;
  height: 23px;
}

.node-eye {
  top: 47.5%;
  left: 62.7%;
}

.node-lgn {
  top: 41%;
  left: 75%;
}

.node-v1 {
  top: 32.5%;
  left: 90.8%;
}

.node-mt {
  top: 32%;
  left: 84%;
}

.node-parietal {
  top: 22%;
  left: 74%;
}

.node-action {
  top: 15%;
  left: 64%;
}

.node-v4 {
  top: 50%;
  left: 89%;
}

.node-loc {
  top: 61%;
  left: 84%;
}

.node-it {
  top: 70%;
  left: 75%;
}

.node-memory {
  top: 76%;
  left: 64%;
}

.node-amygdala {
  top: 49%;
  left: 72%;
  z-index: 8;
}

.node-mt .processing-node-copy {
  transform: translate(-34px, -6px);
}

.node-v1 .processing-node-copy {
  transform: translate(27px, 2px);
}

.node-v4 .processing-node-copy {
  transform: translate(28px, -4px);
}

.node-loc .processing-node-copy {
  transform: translate(-30px, 5px);
}

.processing-stream-label {
  position: absolute;
  z-index: 6;
  display: grid;
  min-width: 230px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 2px 9px;
  border: 1px solid;
  border-radius: 13px;
  padding: 8px 10px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(9px);
}

.processing-stream-label > span {
  grid-row: 1/3;
  font-size: 22px;
  font-weight: 950;
}

.processing-stream-label strong {
  font-size: 15px;
  font-weight: 950;
  line-height: 1.1;
}

.processing-stream-label small {
  font-size: 11px;
  font-weight: 750;
  line-height: 1.2;
}

.processing-stream-label em {
  grid-row: 1/3;
  grid-column: 3;
  border-radius: 999px;
  padding: 5px 7px;
  font-size: 11px;
  font-style: normal;
  font-weight: 950;
}

.stream-label-dorsal {
  top: 4%;
  left: 36%;
  border-color: rgba(34, 211, 238, 0.42);
  background: rgba(8, 47, 73, 0.76);
  color: #cffafe;
}

.stream-label-dorsal em {
  background: rgba(34, 211, 238, 0.16);
  color: #67e8f9;
}

.stream-label-ventral {
  bottom: 3%;
  left: 47%;
  border-color: rgba(192, 132, 252, 0.42);
  background: rgba(59, 7, 100, 0.72);
  color: #f3e8ff;
}

.stream-label-ventral em {
  background: rgba(192, 132, 252, 0.17);
  color: #d8b4fe;
}

.processing-brain-direction {
  position: absolute;
  z-index: 5;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(9, 9, 11, 0.68);
  padding: 5px 8px;
  color: #d4d4d8;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.02em;
  backdrop-filter: blur(7px);
}

.direction-back {
  top: 7%;
  right: 1.5%;
  left: auto;
}

.direction-front {
  top: 49%;
  right: auto;
  left: 48%;
}

.processing-mobile-rails {
  display: none;
}

.processing-impact-strip {
  display: grid;
  grid-template-columns: minmax(150px, 0.7fr) repeat(3, minmax(0, 1fr));
  gap: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(9, 9, 11, 0.9);
  padding: 12px 16px 14px;
}

.processing-impact-title {
  display: grid;
  align-content: center;
  gap: 3px;
}

.processing-impact-title small {
  color: #67e8f9;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.processing-impact-title strong {
  overflow: hidden;
  color: #ffffff;
  font-size: 15px;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.processing-impact-item {
  display: grid;
  min-width: 0;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  padding: 9px;
  color: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;
}

.processing-impact-item:hover,
.processing-impact-item:focus-visible,
.processing-impact-item.is-selected {
  border-color: rgba(103, 232, 249, 0.66);
  background: rgba(8, 145, 178, 0.12);
}

.processing-impact-item:focus-visible {
  outline: 3px solid rgba(103, 232, 249, 0.72);
  outline-offset: 2px;
}

.processing-impact-orb {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border: 1px solid rgba(103, 232, 249, 0.44);
  border-radius: 999px;
  background: rgba(8, 145, 178, 0.15);
  color: #a5f3fc;
  font-size: 11px;
  font-weight: 950;
}

.processing-impact-copy {
  display: grid;
  min-width: 0;
  gap: 6px;
}

.processing-impact-copy strong {
  overflow: hidden;
  color: #f4f4f5;
  font-size: 12px;
  font-weight: 850;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.processing-impact-copy i {
  display: block;
  height: 5px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}

.processing-impact-copy i b {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #0891b2, #67e8f9);
  box-shadow: 0 0 8px rgba(34, 211, 238, 0.62);
  transition: width 220ms ease;
}

.processing-impact-item > em {
  border-radius: 999px;
  padding: 5px 7px;
  font-size: 11px;
  font-style: normal;
  font-weight: 950;
}

.processing-impact-item > em.is-up {
  background: rgba(244, 63, 94, 0.16);
  color: #fda4af;
}

.processing-impact-item > em.is-down {
  background: rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
}

.processing-impact-item > em.is-steady {
  background: rgba(161, 161, 170, 0.16);
  color: #d4d4d8;
}

.processing-details {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: #09090b;
  color: #ffffff;
}

.processing-details summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 15px 18px;
  color: #e4e4e7;
  cursor: pointer;
  list-style: none;
}

.processing-details summary::-webkit-details-marker {
  display: none;
}

.processing-details summary > span {
  display: grid;
  gap: 3px;
}

.processing-details summary strong {
  font-size: 14px;
  font-weight: 900;
}

.processing-details summary small {
  color: #a1a1aa;
  font-size: 12px;
  font-weight: 650;
}

.processing-details summary > b {
  flex: 0 0 auto;
  border: 1px solid rgba(192, 132, 252, 0.32);
  border-radius: 999px;
  background: rgba(168, 85, 247, 0.1);
  padding: 6px 9px;
  color: #d8b4fe;
  font-size: 11px;
  font-weight: 900;
}

.processing-details[open] summary > b {
  color: transparent;
  font-size: 0;
}

.processing-details[open] summary > b::after {
  color: #d8b4fe;
  content: "접기";
  font-size: 11px;
}

.processing-details-body {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 16px 18px 18px;
}

.processing-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.processing-detail-grid > article {
  min-width: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.035);
  padding: 14px;
}

.processing-detail-grid h3 {
  color: #fafafa;
  font-size: 14px;
  font-weight: 900;
}

.processing-detail-grid ol {
  display: grid;
  gap: 10px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.processing-detail-grid li {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr);
  gap: 9px;
}

.processing-detail-grid li > span {
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  border: 1px solid rgba(192, 132, 252, 0.34);
  border-radius: 9px;
  background: rgba(168, 85, 247, 0.1);
  color: #d8b4fe;
  font-size: 11px;
  font-weight: 950;
}

.processing-detail-grid li strong,
.processing-detail-grid li p {
  display: block;
}

.processing-detail-grid li strong {
  color: #e4e4e7;
  font-size: 12px;
  font-weight: 850;
}

.processing-detail-grid li p {
  margin-top: 3px;
  color: #a1a1aa;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
}

.processing-detail-stream {
  margin-top: 10px;
  border-left: 3px solid #c084fc;
  border-radius: 0 10px 10px 0;
  background: rgba(168, 85, 247, 0.08);
  padding: 10px 11px;
}

.processing-detail-stream[data-kind=dorsal] {
  border-left-color: #22d3ee;
  background: rgba(6, 182, 212, 0.08);
}

.processing-detail-stream strong {
  color: #ffffff;
  font-size: 13px;
  font-weight: 900;
}

.processing-detail-stream p,
.processing-detail-stream small {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
}

.processing-detail-stream p {
  color: #d4d4d8;
}

.processing-detail-stream small {
  color: #a1a1aa;
}

.processing-detail-grid aside {
  margin-top: 10px;
  border: 1px solid rgba(251, 191, 36, 0.24);
  border-radius: 11px;
  background: rgba(120, 53, 15, 0.12);
  padding: 10px 11px;
}

.processing-detail-grid aside strong {
  color: #fcd34d;
  font-size: 12px;
  font-weight: 900;
}

.processing-detail-grid aside p {
  margin-top: 4px;
  color: #d4d4d8;
  font-size: 12px;
  line-height: 1.5;
}

.processing-details-footer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 0.8fr);
  gap: 14px;
  margin-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 14px;
}

.processing-details-footer > p {
  color: #a1a1aa;
  font-size: 12px;
  line-height: 1.55;
}

.processing-details-footer nav {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 7px 10px;
}

.processing-details-footer a {
  color: #67e8f9;
  font-size: 11px;
  font-weight: 800;
  text-decoration: none;
}

.processing-details-footer a:hover {
  text-decoration: underline;
}

@keyframes processing-wire-flow {
  to {
    stroke-dashoffset: -29;
  }
}
@keyframes processing-feedback-flow {
  to {
    stroke-dashoffset: 36;
  }
}
@keyframes processing-route-breathe {
  50% {
    filter: drop-shadow(0 0 15px currentColor);
  }
}
@keyframes processing-focus-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes processing-gaze-dot {
  from {
    left: 0;
  }
  to {
    left: 100%;
  }
}
@media (max-width: 1180px) {
  .processing-node {
    width: 94px;
  }
  .processing-node-orb {
    width: 52px;
    height: 52px;
  }
  .processing-node-copy {
    padding: 4px 6px;
  }
  .processing-node-copy strong {
    font-size: 12px;
  }
  .processing-node-copy small {
    font-size: 10px;
  }
  .processing-stream-label {
    min-width: 205px;
  }
}
@media (max-width: 980px) {
  .processing-canvas {
    height: 940px;
    min-height: 940px;
  }
  .processing-network {
    display: none;
  }
  .processing-brain-image {
    top: 34%;
    right: 5%;
    width: 90%;
    height: 55%;
    opacity: 0.34;
    object-position: center;
  }
  .processing-anatomy-note {
    display: none;
  }
  .processing-stimulus-window {
    top: 24px;
    left: 4%;
    width: 70%;
    max-width: none;
    transform: none;
  }
  .processing-focus-lens {
    top: 94px;
    right: 5%;
    left: auto;
    transform: none;
  }
  .processing-gaze-link {
    display: none;
  }
  .processing-node {
    width: 116px;
  }
  .processing-node-orb {
    width: 58px;
    height: 58px;
  }
  .processing-node-copy strong {
    font-size: 14px;
  }
  .processing-node-copy small {
    font-size: 11px;
  }
  .node-mt .processing-node-copy,
  .node-v1 .processing-node-copy,
  .node-v4 .processing-node-copy,
  .node-loc .processing-node-copy {
    transform: none;
  }
  .node-eye,
  .node-lgn,
  .node-v1 {
    top: 28%;
  }
  .node-eye {
    left: 17%;
  }
  .node-lgn {
    left: 50%;
  }
  .node-v1 {
    left: 83%;
  }
  .stream-label-dorsal,
  .stream-label-ventral {
    top: 36%;
    bottom: auto;
    left: 5%;
    width: 42%;
    min-width: 0;
  }
  .stream-label-ventral {
    left: 53%;
  }
  .node-mt,
  .node-v4 {
    top: 47%;
  }
  .node-parietal,
  .node-loc {
    top: 59%;
  }
  .node-action,
  .node-it {
    top: 71%;
  }
  .node-memory {
    top: 83%;
    left: 74%;
  }
  .node-mt,
  .node-parietal,
  .node-action {
    left: 26%;
  }
  .node-v4,
  .node-loc,
  .node-it {
    left: 74%;
  }
  .node-amygdala {
    top: 91%;
    left: 50%;
  }
  .processing-brain-direction {
    display: none;
  }
  .processing-mobile-rails {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: block;
    pointer-events: none;
  }
  .processing-mobile-rails i {
    position: absolute;
    display: block;
    background: currentColor;
    box-shadow: 0 0 9px currentColor;
  }
  .processing-mobile-rails i:first-child {
    top: 28%;
    right: 17%;
    left: 17%;
    height: 4px;
    color: #fbbf24;
  }
  .processing-mobile-rails i:nth-child(2) {
    top: 42%;
    bottom: 25%;
    left: 26%;
    width: 4px;
    color: #22d3ee;
  }
  .processing-mobile-rails i:nth-child(3) {
    top: 42%;
    bottom: 13%;
    left: 74%;
    width: 4px;
    color: #c084fc;
  }
  .processing-impact-strip {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .processing-impact-title {
    grid-column: 1/-1;
  }
  .processing-detail-grid,
  .processing-details-footer {
    grid-template-columns: minmax(0, 1fr);
  }
  .processing-details-footer nav {
    justify-content: flex-start;
  }
}
@media (max-width: 640px) {
  .processing-canvas {
    height: 900px;
    min-height: 900px;
  }
  .processing-stimulus-window {
    top: 18px;
    left: 4%;
    width: 70%;
  }
  .processing-stimulus-window .stimulus-focus-marker {
    width: 42px;
    height: 42px;
  }
  .processing-focus-lens {
    top: 62px;
    right: 4%;
    width: 68px;
    height: 68px;
  }
  .processing-node {
    width: 102px;
  }
  .processing-node-orb {
    width: 52px;
    height: 52px;
  }
  .processing-node-copy {
    min-width: 92px;
    padding: 4px 5px;
  }
  .processing-node-copy strong {
    font-size: 12px;
  }
  .processing-node-copy small {
    font-size: 10px;
  }
  .processing-output-node .processing-node-orb {
    width: 62px;
    height: 62px;
  }
  .processing-stream-label {
    grid-template-columns: auto minmax(0, 1fr);
    padding: 7px 8px;
  }
  .processing-stream-label > span {
    font-size: 18px;
  }
  .processing-stream-label strong {
    font-size: 13px;
  }
  .processing-stream-label small {
    font-size: 10px;
  }
  .processing-stream-label em {
    display: none;
  }
  .processing-impact-strip {
    grid-template-columns: minmax(0, 1fr);
    padding: 12px;
  }
  .processing-impact-title {
    grid-column: auto;
  }
  .processing-impact-item {
    min-height: 58px;
  }
  .processing-details summary {
    align-items: flex-start;
    padding: 14px;
  }
  .processing-details summary small {
    font-size: 11px;
  }
  .processing-details-body {
    padding: 14px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .processing-wire-input,
  .processing-wire-dorsal,
  .processing-wire-ventral,
  .processing-wire-feedback,
  .processing-wire-salience,
  .processing-focus-lens::after,
  .processing-gaze-link i {
    animation: none;
  }
  .processing-node-orb,
  .processing-impact-item,
  .processing-impact-copy i b {
    transition: none;
  }
}`],
})
export class PageDashboardComponent implements OnInit {
    public selectedStimulusId: string = 'hangul';
    public selectedFocusId: string = 'hangul-first-word';
    public compareStimulusId: string = 'landscape';
    public selectedRegionId: string = 'vwfa';
    public viewMode: '2d' | '3d' = '3d';
    public brainAngle: number = -10;
    public comparisonEnabled: boolean = true;
    public showConnections: boolean = true;

    public commonPathway: EarlyPathwayNode[] = [
        { label: '망막', detail: '광수용기 → 양극세포 → 신경절세포' },
        { label: '시신경', detail: '각 눈의 신경절세포 축삭을 전달' },
        { label: '시각교차', detail: '코쪽 망막 섬유가 반대편으로 교차' },
        { label: '시삭', detail: '반대쪽 시야 정보를 묶어 전달' },
        { label: '외측슬상핵', detail: '시상 LGN에서 M·P·K 신호 중계' },
        { label: '시방사', detail: 'LGN 신호를 후두엽으로 투사' },
        { label: 'V1', detail: '경계·방향·위치의 초기 시각 지도' }
    ];

    public recognitionStages: RecognitionStage[] = [
        {
            id: 'features',
            step: '01',
            region: 'V1·V2 / V4·VO',
            title: '경계·방향·색·표면 분석',
            description: '망막 좌표를 유지한 채 국소 대비와 방향을 분해하고, 색·곡률·표면 단서를 병렬로 정교화합니다.'
        },
        {
            id: 'loc',
            step: '02',
            region: '외측 후두 복합체 LOC',
            title: '부분을 온전한 형태로 통합',
            description: 'LO와 뒤쪽 방추 영역(pFs)을 포함하는 대상 선택 영역군이 윤곽과 부분 관계를 물체·형태 후보로 묶습니다.'
        },
        {
            id: 'it',
            step: '03',
            region: '후방 → 전방 VTC / IT 계열',
            title: '범주와 정체성을 더 안정적으로 표현',
            description: '앞쪽으로 갈수록 복잡한 특징 조합과 범주 선택성, 시점·크기 변화에 대한 허용도가 커지는 경향이 있습니다.'
        },
        {
            id: 'memory',
            step: '04',
            region: '전측두엽·주변피질 + 기억망',
            title: '개념·용도·익숙함과 연결',
            description: '세밀한 구분이 필요할 때 물체 표상을 분산된 의미·일화 기억과 대조해 이름, 용도와 맥락을 구체화합니다.'
        }
    ];

    public streamComparisons: StreamComparison[] = [
        {
            kind: 'ventral',
            label: '복측 · 후두측두 경로',
            question: '무엇인가?',
            route: 'V4/VO · LOC → 복측 후두측두피질/IT → 전측두·기억망',
            output: '형태, 범주, 정체성, 의미와 익숙함',
            awareness: '보고 가능한 물체 지각·재인과 더 밀접하지만 초기 처리는 자각 전에 시작될 수 있습니다.'
        },
        {
            kind: 'dorsal',
            label: '배측 · 후두두정 경로',
            question: '어디에 있고 어떻게 다룰까?',
            route: 'V3A · MT/MST → IPS/PPC → 전두·운동 조절망',
            output: '위치, 방향, 속도, 시선·손·몸을 위한 좌표와 온라인 행동',
            awareness: '빠르고 자동적인 시각운동 변환에 강점이 있지만 의식적 공간 지각과 주의에도 관여합니다.'
        }
    ];

    public objectProcessingEvidenceIds: string[] = [
        'object-loc',
        'ventral-hierarchy',
        'object-concepts',
        'eye-attention',
        'stream-awareness'
    ];

    public stimuli: VisualStimulus[] = [
        {
            id: 'hangul',
            label: '한글 읽기',
            subtitle: '문자 · 음운',
            category: '문자',
            imageSrc: '/assets/stimuli/hangul-v1.webp',
            imageAlt: '봄날의 햇살이라는 한글 문구가 인쇄된 읽기 카드',
            signal: '획·자모 대비',
            summary: '시각적 글자 형태를 빠르게 구분한 뒤 음운과 의미로 변환하는 좌측 언어 네트워크가 주로 관여합니다.',
            task: '짧은 한글 단어를 소리 내지 않고 읽는 과제',
            elements: ['글자 형태', '자모 결합', '음운 변환', '문맥 의미'],
            focusPoints: [
                {
                    id: 'hangul-first-word',
                    label: '‘봄날’ 글자',
                    cue: '문자 앞부분',
                    description: '첫 음절부터 글자 형태와 읽기 순서를 따라갑니다.',
                    processing: {
                        features: '검은 획의 방향·교차점과 흰 배경의 대비가 중심시야에 들어옵니다.',
                        form: '자음·모음 획을 두 음절 블록으로 묶어 익숙한 한글 단어 형태를 구성합니다.',
                        memory: '‘봄날’의 철자 표상을 음운, 계절 개념과 저장된 어휘 기억에 대조합니다.',
                        action: '왼쪽 음절에 주의를 고정한 뒤 읽기 순서에 맞춰 오른쪽 단어로 다음 시선을 준비합니다.'
                    },
                    x: 36,
                    y: 52,
                    activationDeltas: { v1: 3, vwfa: 4, wernicke: -5, parietal: 2 }
                },
                {
                    id: 'hangul-last-word',
                    label: '‘햇살’ 글자',
                    cue: '문자 뒷부분',
                    description: '뒤 단어의 의미를 완성하며 언어 이해 반응이 상대적으로 커집니다.',
                    processing: {
                        features: '연속된 글자 획과 음절 사이 간격, 행 안의 위치 정보가 선택됩니다.',
                        form: '세 음절의 배열을 하나의 단어 형태로 묶고 앞 단어와 같은 문장 단위로 통합합니다.',
                        memory: '‘햇살’의 철자·발음·의미를 인출해 ‘봄날의 햇살’이라는 구절의 의미를 완성합니다.',
                        action: '현재 행의 끝을 확인하고 다음 읽을 위치나 장면의 다른 요소로 시선을 옮길 준비를 합니다.'
                    },
                    x: 64,
                    y: 51,
                    activationDeltas: { v1: 1, vwfa: -2, wernicke: 7, temporal: 4, broca: 3 }
                },
                {
                    id: 'hangul-pencil',
                    label: '연필',
                    cue: '주변 사물',
                    description: '글자 밖 사물로 시선을 옮겨 형태·공간 주의의 비중을 높입니다.',
                    processing: {
                        features: '가늘고 긴 대각선 윤곽, 노란 표면과 뾰족한 끝의 대비가 선택됩니다.',
                        form: '몸통·나무 끝·심의 부분 관계를 하나의 길쭉한 도구 형태로 묶습니다.',
                        memory: '형태를 ‘연필’ 범주와 연결하고 쓰기 경험, 손 사용법 같은 지식과 대조합니다.',
                        action: '연필의 방향과 끝 위치를 계산해 집기·가리키기 같은 가능한 손 동작을 준비합니다.'
                    },
                    x: 14,
                    y: 13,
                    activationDeltas: { v1: 2, v4: 8, vwfa: -18, wernicke: -15, temporal: 5, parietal: 5, pfc: 4 }
                }
            ],
            activations: {
                v1: 82, v4: 64, mt: 18, vwfa: 96, ffa: 20, broca: 72,
                wernicke: 90, parietal: 66, ppa: 16, temporal: 74, pfc: 54, amygdala: 20
            },
            reasons: {
                v1: '글자의 획과 경계, 대비를 시각 지도에 배치합니다.',
                vwfa: '반복 학습된 한글 철자 패턴을 빠르게 단어 형태로 묶습니다.',
                broca: '글자를 음운으로 변환하고 발화 계획을 준비할 때 관여합니다.',
                wernicke: '읽은 단어의 어휘와 문장 의미를 해석하는 과정에 관여합니다.',
                parietal: '자모의 순서와 시공간적 주의를 유지하는 데 보조적으로 관여합니다.'
            }
        },
        {
            id: 'hanja',
            label: '한자 읽기',
            subtitle: '형태 · 의미',
            category: '문자',
            imageSrc: '/assets/stimuli/hanja-v1.webp',
            imageAlt: '산 산자와 물 수자가 적힌 한자 학습 카드',
            signal: '복합 획·공간 배치',
            summary: '복잡한 시각 형태와 학습된 의미를 연결하기 위해 시각단어형태영역, 측두엽, 두정엽 반응이 함께 증가합니다.',
            task: '복잡도가 다른 한자의 음과 뜻을 판단하는 과제',
            elements: ['복잡한 획', '형태 기억', '음독', '의미 회상'],
            focusPoints: [
                {
                    id: 'hanja-mountain',
                    label: '山(산)',
                    cue: '왼쪽 문자',
                    description: '첫 문자의 복합 획을 분해하고 학습된 뜻과 연결합니다.',
                    processing: {
                        features: '세로획과 갈라지는 사선, 획 사이의 간격과 교차 관계가 중심시야에서 분해됩니다.',
                        form: '세 획의 공간 배치를 학습된 표의문자 ‘山’의 전체 형태로 묶습니다.',
                        memory: '문자 형태를 음 ‘산’, 산이라는 개념과 장기 어휘·의미 기억에 대조합니다.',
                        action: '왼쪽 문자의 획을 순서대로 확인한 뒤 오른쪽 문자로 다음 시선을 이동합니다.'
                    },
                    x: 39,
                    y: 51,
                    activationDeltas: { v1: 2, v4: 3, vwfa: 4, temporal: 4, parietal: -2 }
                },
                {
                    id: 'hanja-water',
                    label: '水(수)',
                    cue: '오른쪽 문자',
                    description: '두 번째 문자의 형태를 의미 기억과 연결하는 비중이 커집니다.',
                    processing: {
                        features: '중심 세로획과 양옆의 비대칭 사선, 획 방향의 차이가 선택됩니다.',
                        form: '복잡한 획 배열을 하나의 문자 ‘水’로 통합하고 유사한 글자 후보와 구분합니다.',
                        memory: '형태를 음 ‘수’와 물의 의미, 앞선 ‘山’과 함께 학습한 조합 기억에 연결합니다.',
                        action: '오른쪽 문자에 정밀 주의를 유지해 획 관계를 확인하고 단어 단위 판단을 마칩니다.'
                    },
                    x: 62,
                    y: 49,
                    activationDeltas: { v1: 1, v4: 5, vwfa: 2, temporal: 7, parietal: 2, wernicke: 2 }
                },
                {
                    id: 'hanja-brush',
                    label: '붓',
                    cue: '주변 사물',
                    description: '문자에서 붓의 곡선과 방향으로 초점을 옮겨 시각 형태 분석을 강조합니다.',
                    processing: {
                        features: '긴 손잡이의 대각선, 어두운 털 끝과 배경 사이의 윤곽이 선택됩니다.',
                        form: '손잡이와 붓털의 부분 관계를 가늘고 긴 필기 도구의 입체 형태로 묶습니다.',
                        memory: '형태를 ‘붓’과 연결하고 서예, 먹, 잡는 방법에 관한 시각·동작 지식을 인출합니다.',
                        action: '손잡이 방향과 붓끝 위치를 계산해 집기·쓰기 같은 가능한 동작을 예측합니다.'
                    },
                    x: 79,
                    y: 9,
                    activationDeltas: { v1: 3, v4: 8, vwfa: -15, temporal: -8, parietal: 6, pfc: 3 }
                }
            ],
            activations: {
                v1: 86, v4: 72, mt: 16, vwfa: 91, ffa: 24, broca: 64,
                wernicke: 82, parietal: 80, ppa: 18, temporal: 88, pfc: 68, amygdala: 24
            },
            reasons: {
                v1: '조밀한 획과 공간 배치를 세밀하게 분해합니다.',
                v4: '곡선, 교차점, 복합 형태를 안정된 글자 형태로 통합합니다.',
                vwfa: '학습된 표기 형태를 익숙한 문자 패턴으로 판별합니다.',
                temporal: '한자 형태와 저장된 개념·의미 기억을 연결합니다.',
                parietal: '복잡한 획의 위치 관계와 주의 이동을 조절합니다.'
            }
        },
        {
            id: 'face',
            label: '얼굴 보기',
            subtitle: '정체성 · 표정',
            category: '사람',
            imageSrc: '/assets/stimuli/face-v1.webp',
            imageAlt: '두 눈과 얼굴 윤곽이 또렷한 중립 표정의 성인 얼굴',
            signal: '눈·입 배치와 표정',
            summary: '얼굴의 전체 배치와 눈·입의 관계를 통합하며 방추상회 얼굴 영역과 정서 네트워크 반응이 두드러집니다.',
            task: '익숙한 얼굴과 낯선 얼굴, 표정을 구분하는 과제',
            elements: ['눈과 시선', '얼굴 윤곽', '표정', '정체성'],
            focusPoints: [
                {
                    id: 'face-eyes',
                    label: '눈과 시선',
                    cue: '상단 특징',
                    description: '두 눈의 방향과 대칭을 비교해 시선·공간 주의를 강화합니다.',
                    processing: {
                        features: '눈 흰자·홍채·눈꺼풀의 대비와 두 눈의 대칭, 홍채 방향이 정밀 분석됩니다.',
                        form: '두 눈을 얼굴 내부의 한 쌍으로 묶고 머리 방향과 함께 상대의 시선 단서를 구성합니다.',
                        memory: '얼굴 정체성 후보와 익숙함, 시선이 향한 대상에 관한 사회적 맥락을 대조합니다.',
                        action: '상대의 시선 방향을 따라 주의를 재배치하거나 눈·입 사이로 다음 시선을 계획합니다.'
                    },
                    x: 51,
                    y: 38,
                    activationDeltas: { v1: 4, ffa: 2, parietal: 6, amygdala: 3 }
                },
                {
                    id: 'face-mouth',
                    label: '입과 표정',
                    cue: '하단 특징',
                    description: '입 모양의 미세한 표정 단서를 읽어 정서적 중요도를 더 평가합니다.',
                    processing: {
                        features: '입술 윤곽, 입꼬리 각도와 주변 명암의 미세한 차이가 선택됩니다.',
                        form: '입을 얼굴 전체 배치 안의 표정 구성 요소로 묶어 중립·미소 같은 후보를 만듭니다.',
                        memory: '현재 표정을 과거의 정서 범주와 사람·상황에 대한 사회적 기억에 대조합니다.',
                        action: '표정의 중요도에 따라 눈으로 재확인하거나 접근·회피와 사회적 반응을 조절합니다.'
                    },
                    x: 51,
                    y: 59,
                    activationDeltas: { v4: 4, ffa: -2, temporal: 5, amygdala: 10 }
                },
                {
                    id: 'face-contour',
                    label: '얼굴 윤곽',
                    cue: '전체 배치',
                    description: '윤곽과 내부 특징의 전체 배치를 하나의 얼굴로 통합합니다.',
                    processing: {
                        features: '머리카락과 피부의 경계, 타원형 윤곽과 내부 특징의 상대 위치가 선택됩니다.',
                        form: '윤곽·눈·코·입을 하나의 전체 얼굴 구성으로 통합해 시점이 달라도 유지되는 형태를 만듭니다.',
                        memory: '전체 얼굴 표상을 익숙한 사람의 정체성 또는 낯선 얼굴 범주와 비교합니다.',
                        action: '얼굴 중심에 시선을 안정시키고 정체성 판단에 필요한 눈·입 특징으로 재주시합니다.'
                    },
                    x: 36,
                    y: 48,
                    activationDeltas: { v1: 2, v4: 3, ffa: 2, temporal: 4, parietal: -2 }
                }
            ],
            activations: {
                v1: 80, v4: 72, mt: 42, vwfa: 16, ffa: 98, broca: 16,
                wernicke: 20, parietal: 42, ppa: 24, temporal: 88, pfc: 58, amygdala: 76
            },
            reasons: {
                v1: '얼굴 윤곽과 눈·입의 국소 대비를 초기 분석합니다.',
                ffa: '얼굴의 전체적인 배치와 정체성 단서에 선택적으로 민감합니다.',
                temporal: '얼굴을 사람에 대한 의미·사회적 기억과 연결합니다.',
                amygdala: '표정의 정서적 중요도와 위협 가능성을 빠르게 평가합니다.',
                pfc: '상황에 맞는 사회적 판단과 주의 조절을 지원합니다.'
            }
        },
        {
            id: 'landscape',
            label: '풍경 보기',
            subtitle: '장면 · 공간',
            category: '공간',
            imageSrc: '/assets/stimuli/landscape-v1.webp',
            imageAlt: '지평선과 멀리 이어진 산, 골짜기 길이 보이는 풍경',
            signal: '원근·경계·이동 경로',
            summary: '넓은 장면의 구조, 깊이, 경계를 통합하면서 해마방회 장소 영역과 두정엽의 공간 지도가 강하게 관여합니다.',
            task: '실내·실외 장면의 종류와 이동 가능한 경로를 판단하는 과제',
            elements: ['원근', '지평선', '장면 경계', '이동 경로'],
            focusPoints: [
                {
                    id: 'landscape-mountain',
                    label: '먼 산',
                    cue: '장면 정체성',
                    description: '산맥의 윤곽과 원근을 이용해 장면의 종류와 깊이를 파악합니다.',
                    processing: {
                        features: '지평선의 굴곡, 산 능선의 방향과 멀수록 옅어지는 질감·명암이 선택됩니다.',
                        form: '여러 능선과 하늘의 경계를 하나의 먼 산맥과 넓은 장면 구조로 통합합니다.',
                        memory: '형태를 ‘산악 풍경’ 범주와 연결하고 익숙한 장소·경관 기억과 대조합니다.',
                        action: '지평선을 기준으로 몸의 방향과 장면 깊이를 추정하고 가까운 이동 경로로 시선을 옮깁니다.'
                    },
                    x: 64,
                    y: 34,
                    activationDeltas: { v1: 2, v4: 5, ppa: 3, temporal: 3 }
                },
                {
                    id: 'landscape-trail',
                    label: '오솔길',
                    cue: '이동 경로',
                    description: '가까운 길의 방향과 이동 가능성을 계산해 공간·행동 반응을 높입니다.',
                    processing: {
                        features: '양쪽 경계선의 수렴, 표면 질감과 가까운 곳에서 넓어지는 원근 단서가 선택됩니다.',
                        form: '끊기지 않은 바닥 표면을 주변 지형과 분리해 ‘걸을 수 있는 길’ 형태로 묶습니다.',
                        memory: '현재 길을 통행 가능한 경로와 과거의 산책로·방향 기억에 대조합니다.',
                        action: '길의 중심선과 굽은 방향을 몸 중심 좌표로 바꿔 다음 발걸음과 시선 위치를 계획합니다.'
                    },
                    x: 67,
                    y: 76,
                    activationDeltas: { mt: 4, ppa: -6, parietal: 11, pfc: 7 }
                },
                {
                    id: 'landscape-river',
                    label: '계곡의 강',
                    cue: '깊이 단서',
                    description: '굽이치는 강을 따라 시선을 이동하며 장면 구조와 거리 정보를 함께 봅니다.',
                    processing: {
                        features: '밝게 반사되는 수면, 굽은 경계와 폭이 멀수록 좁아지는 원근 단서가 선택됩니다.',
                        form: '분절된 밝은 영역을 계곡을 따라 이어지는 하나의 물길로 통합합니다.',
                        memory: '형태를 강·계곡 범주와 연결하고 장면의 지형 및 장소 기억과 대조합니다.',
                        action: '물길의 굴곡을 따라 시선을 이동하며 거리, 경계와 건널 수 있는 방향을 계산합니다.'
                    },
                    x: 54,
                    y: 48,
                    activationDeltas: { v1: 3, mt: 3, ppa: 1, parietal: 8 }
                }
            ],
            activations: {
                v1: 84, v4: 74, mt: 48, vwfa: 12, ffa: 18, broca: 10,
                wernicke: 16, parietal: 84, ppa: 97, temporal: 60, pfc: 44, amygdala: 34
            },
            reasons: {
                v1: '넓은 시야의 경계와 방향 정보를 시각 지도에 배치합니다.',
                ppa: '장소의 전체 구조와 장면 정체성을 인식하는 데 강하게 관여합니다.',
                parietal: '거리, 방향, 이동 가능 경로를 행동 중심의 공간 지도로 변환합니다.',
                temporal: '장면을 익숙한 장소나 범주 기억과 연결합니다.',
                pfc: '탐색 목표에 따라 주의를 배분하고 경로 선택을 지원합니다.'
            }
        },
        {
            id: 'motion',
            label: '움직임 보기',
            subtitle: '속도 · 방향',
            category: '움직임',
            imageSrc: '/assets/stimuli/motion-v1.webp',
            imageAlt: '배경이 흐르는 공원 길을 옆으로 지나가는 자전거 이용자',
            signal: '모션 블러·자세 단서',
            summary: '정지 사진의 자세와 배경 흐림에서 이동을 추론할 때 MT/MST와 후두정엽의 등쪽 시각 경로가 관여할 수 있습니다.',
            task: '정지 장면에 암시된 이동 방향과 다음 위치를 추정하는 과제',
            elements: ['모션 블러', '자세', '암시된 방향', '다음 위치'],
            focusPoints: [
                {
                    id: 'motion-rider',
                    label: '자전거 이용자',
                    cue: '움직이는 사람',
                    description: '사람의 자세와 진행 의도를 읽어 대상·공간 판단의 비중을 높입니다.',
                    processing: {
                        features: '몸·자전거의 윤곽, 기울어진 자세와 배경 흐림 사이의 대비가 선택됩니다.',
                        form: '사람의 몸과 자전거 부품을 결합된 하나의 이동 주체 형태로 묶습니다.',
                        memory: '형태를 ‘자전거를 타는 사람’ 범주와 교통 상황·행동 의도 지식에 연결합니다.',
                        action: '자세와 진행 방향에서 다음 위치를 예측해 추적 시선과 회피 가능한 공간을 준비합니다.'
                    },
                    x: 49,
                    y: 29,
                    activationDeltas: { mt: -12, ffa: 18, temporal: 7, parietal: 4, amygdala: 4 }
                },
                {
                    id: 'motion-wheel',
                    label: '앞바퀴',
                    cue: '운동 방향',
                    description: '바퀴와 도로의 접점을 기준으로 속도와 다음 위치를 추정합니다.',
                    processing: {
                        features: '원형 테두리, 살의 방사 패턴과 도로 접점의 국소 대비가 선택됩니다.',
                        form: '원과 프레임의 연결 관계를 자전거의 앞바퀴라는 부품 형태로 통합합니다.',
                        memory: '바퀴 형태를 회전·주행 경험과 연결해 이동 방향과 물체 정체성 판단을 보강합니다.',
                        action: '접점과 바퀴 방향을 기준으로 회전·속도와 다음 위치를 추정해 시선을 선행 이동합니다.'
                    },
                    x: 59,
                    y: 70,
                    activationDeltas: { v1: 3, mt: 2, parietal: 6, pfc: 3 }
                },
                {
                    id: 'motion-background',
                    label: '흐린 배경',
                    cue: '모션 블러',
                    description: '나무의 수평 흐림을 이용해 장면 전체의 상대 움직임을 추론합니다.',
                    processing: {
                        features: '수평으로 늘어진 질감, 방향성 있는 흐림과 낮은 공간주파수 변화가 선택됩니다.',
                        form: '개별 나무보다 연속된 공원 배경과 광류 패턴으로 장면을 통합합니다.',
                        memory: '배경 구조를 공원·가로수 장면 기억과 비교해 물체 이동인지 카메라 이동인지 해석합니다.',
                        action: '광류 방향을 몸·시선 기준 좌표로 바꿔 상대 속도와 진행 방향을 보정합니다.'
                    },
                    x: 82,
                    y: 30,
                    activationDeltas: { v1: 6, mt: 2, parietal: 1, ppa: 7 }
                }
            ],
            activations: {
                v1: 88, v4: 46, mt: 98, vwfa: 10, ffa: 18, broca: 10,
                wernicke: 12, parietal: 92, ppa: 50, temporal: 46, pfc: 62, amygdala: 44
            },
            reasons: {
                v1: '정지 사진의 경계, 방향, 모션 블러를 초기 분석합니다.',
                mt: '자세와 배경 흐림이 암시하는 이동 방향과 다음 위치를 추론합니다.',
                parietal: '추정한 대상 위치를 행동과 공간 주의에 연결합니다.',
                pfc: '다음 위치와 충돌 가능성을 예측하며 반응 전략을 유지합니다.',
                amygdala: '접근하는 것으로 해석된 대상의 잠재적 중요도를 평가합니다.'
            }
        },
        {
            id: 'danger',
            label: '위험 장면',
            subtitle: '위협 · 판단',
            category: '정서',
            imageSrc: '/assets/stimuli/danger-v1.webp',
            imageAlt: '비 오는 저녁 횡단보도 앞으로 자전거가 접근하는 잠재적 위험 장면',
            signal: '위협·접근·정지 단서',
            summary: '잠재적 위협을 빠르게 탐지한 뒤 변연계와 전전두엽이 정서적 중요도와 대응 행동을 함께 평가합니다.',
            task: '교통사고나 추락 위험 장면에서 위험 요소를 찾는 과제',
            elements: ['위협 단서', '접근 속도', '표정', '상황 맥락'],
            focusPoints: [
                {
                    id: 'danger-cyclist',
                    label: '접근하는 자전거',
                    cue: '동적 위험',
                    description: '접근 방향과 충돌 가능성에 초점을 맞춰 움직임·회피 계산을 강조합니다.',
                    processing: {
                        features: '젖은 도로 위 인물·자전거 윤곽, 접근 방향과 흐림 단서가 빠르게 선택됩니다.',
                        form: '사람과 자전거의 부분을 하나의 접근하는 이동 주체로 묶고 배경에서 분리합니다.',
                        memory: '대상을 ‘접근 차량·자전거’ 범주와 교통 위험 경험, 충돌 사례 기억에 대조합니다.',
                        action: '진행 벡터와 충돌 예상 시간을 계산해 시선을 고정하고 정지·회피 방향을 준비합니다.'
                    },
                    x: 20,
                    y: 48,
                    activationDeltas: { mt: 20, parietal: 10, pfc: 2, amygdala: -8 }
                },
                {
                    id: 'danger-pedestrian',
                    label: '보행자',
                    cue: '행동 판단',
                    description: '보행자의 위치와 다음 행동을 예측해 판단·정서 네트워크를 함께 사용합니다.',
                    processing: {
                        features: '몸 윤곽, 보행 자세와 도로·횡단보도 안의 상대 위치가 선택됩니다.',
                        form: '팔다리와 몸통을 하나의 사람 형태로 통합하고 이동 방향 단서를 함께 묶습니다.',
                        memory: '대상을 ‘보행자’와 연결하고 횡단 규칙, 의도와 사회적 상황 기억을 적용합니다.',
                        action: '보행자의 다음 위치와 내 이동 경로의 교차 여부를 예측해 양보·정지 행동을 조정합니다.'
                    },
                    x: 68,
                    y: 50,
                    activationDeltas: { ffa: 4, parietal: 4, pfc: 8, amygdala: 1 }
                },
                {
                    id: 'danger-signal',
                    label: '빨간 신호',
                    cue: '규칙 단서',
                    description: '정지 신호의 색과 규칙 의미를 읽어 행동 억제와 판단 반응을 높입니다.',
                    processing: {
                        features: '작고 강한 빨간빛의 색·밝기 대비와 원형 경계가 주변 장면에서 두드러집니다.',
                        form: '빛의 배열과 신호등 구조를 하나의 교통 제어 표지 형태로 통합합니다.',
                        memory: '빨간 신호를 ‘정지’ 규칙, 도로 안전 지식과 빠르게 연결합니다.',
                        action: '신호 위치를 기준으로 진행 행동을 억제하고 도로와 이동 대상들을 다시 확인합니다.'
                    },
                    x: 88,
                    y: 9,
                    activationDeltas: { v4: 15, parietal: -4, pfc: 10, amygdala: -10 }
                }
            ],
            activations: {
                v1: 84, v4: 62, mt: 74, vwfa: 10, ffa: 42, broca: 14,
                wernicke: 20, parietal: 80, ppa: 58, temporal: 70, pfc: 90, amygdala: 99
            },
            reasons: {
                amygdala: '위협 가능성과 정서적 중요도를 빠르게 평가해 주의를 끌어옵니다.',
                pfc: '충동적 반응을 조절하고 상황에 맞는 대응 행동을 선택합니다.',
                mt: '빠르게 접근하거나 충돌하는 움직임 단서를 추적합니다.',
                parietal: '위험 대상의 위치와 회피 방향을 공간적으로 계산합니다.',
                ffa: '장면 속 얼굴의 공포·분노 표정을 구분할 때 관여합니다.'
            }
        },
        {
            id: 'color',
            label: '강한 색채',
            subtitle: '색상 · 대비',
            category: '색상',
            imageSrc: '/assets/stimuli/color-v1.webp',
            imageAlt: '빨강, 파랑, 노랑, 초록색 종이 도형이 강하게 대비되는 장면',
            signal: '채도·색 대비',
            summary: '채도와 색 대비가 큰 장면은 초기 시각피질과 V4의 색채·표면 통합 반응을 크게 높입니다.',
            task: '고채도 색상 조합에서 목표 색을 빠르게 찾는 과제',
            elements: ['채도', '색 대비', '표면', '시각적 현저성'],
            focusPoints: [
                {
                    id: 'color-red',
                    label: '빨간 원',
                    cue: '따뜻한 색',
                    description: '빨간 원의 채도와 같은 색 배경 사이의 경계를 비교합니다.',
                    processing: {
                        features: '강한 적색 채도, 원형 경계와 인접 표면의 밝기 차이가 선택됩니다.',
                        form: '색 경계가 닫힌 하나의 원형 표면으로 묶여 배경의 다른 도형과 분리됩니다.',
                        memory: '표면을 ‘빨간 원’ 범주와 연결하고 색 이름·경고·따뜻함 같은 학습된 연상을 활성화합니다.',
                        action: '목표색의 위치에 주의를 유지하고 같은 색 또는 대비되는 다음 도형으로 탐색을 이어갑니다.'
                    },
                    x: 20,
                    y: 26,
                    activationDeltas: { v1: 2, v4: 1, parietal: 2, amygdala: 3 }
                },
                {
                    id: 'color-green',
                    label: '초록 원',
                    cue: '보색 대비',
                    description: '노란 배경 위 초록 원을 찾아 색 대비와 위치 선택을 강조합니다.',
                    processing: {
                        features: '노란 표면 위 초록색의 색상 차이, 닫힌 원 경계와 위치가 선택됩니다.',
                        form: '겹친 색면에서 초록 영역을 독립된 원형 물체로 분절합니다.',
                        memory: '형태와 색을 ‘초록 원’으로 범주화하고 목표색 규칙이나 자연물의 색 연상과 대조합니다.',
                        action: '주변 도형 사이에서 목표 위치를 선택하고 탐색 순서에 맞춰 다음 색으로 시선을 옮깁니다.'
                    },
                    x: 86,
                    y: 45,
                    activationDeltas: { v1: 3, v4: -3, parietal: 8, pfc: 6 }
                },
                {
                    id: 'color-blue',
                    label: '파란 원',
                    cue: '형태 경계',
                    description: '파란 원과 노란 삼각형의 겹친 경계를 세밀하게 구분합니다.',
                    processing: {
                        features: '파랑·노랑의 색 대비, 원과 삼각형이 만나는 T자형 경계가 정밀 분석됩니다.',
                        form: '겹침에도 원의 연속 윤곽을 보완해 파란 원과 앞뒤 도형을 분리합니다.',
                        memory: '완성된 형태를 ‘파란 원’으로 범주화하고 색 이름과 도형 규칙 기억에 연결합니다.',
                        action: '가려진 경계의 위치와 깊이 순서를 계산해 원의 나머지 윤곽으로 시선을 이동합니다.'
                    },
                    x: 39,
                    y: 80,
                    activationDeltas: { v1: 4, v4: -1, parietal: 4, temporal: 2 }
                }
            ],
            activations: {
                v1: 88, v4: 99, mt: 28, vwfa: 14, ffa: 30, broca: 10,
                wernicke: 12, parietal: 56, ppa: 38, temporal: 52, pfc: 46, amygdala: 40
            },
            reasons: {
                v1: '색 경계와 밝기 대비를 국소적으로 분해합니다.',
                v4: '색상 항상성, 채도, 표면색을 통합하는 데 핵심적으로 관여합니다.',
                parietal: '주변보다 두드러진 색 위치로 시각적 주의를 이동시킵니다.',
                temporal: '색과 사물 범주·학습된 의미를 연결합니다.',
                amygdala: '강한 색의 정서적 현저성을 상황에 따라 평가합니다.'
            }
        }
    ];

    public pathwayProfiles: { [key: string]: VisualPathwayProfile } = {
        hangul: {
            title: '배쪽 문자 경로 → 음운망',
            channel: '세밀한 획·대비 · P 채널 기여가 상대적으로 큼',
            overview: '공통 V1 입력 뒤 학습된 한글 형태를 좌측 복측 후두측두피질에서 묶고, 자소-음소 변환과 언어 이해 네트워크로 연결합니다.',
            note: '숙련 독자가 화면의 글자를 실제로 읽는 과제를 가정합니다. 단순히 보기만 하면 두정·언어망 관여는 달라질 수 있습니다.',
            evidenceIds: ['visual-main', 'dual-stream', 'reading-hangul-hanja', 'word-form'],
            branches: [
                {
                    kind: 'ventral',
                    emphasis: 'primary',
                    badge: '주 경로',
                    label: '배쪽 문자·형태 경로',
                    nodes: ['V1', 'V2/V4', '좌측 VWFA', '측두 언어망'],
                    description: '획과 글자열을 익숙한 철자 형태와 어휘·의미 표상으로 변환합니다.'
                },
                {
                    kind: 'dorsal',
                    emphasis: 'support',
                    badge: '기능 연결',
                    label: '두정-음운 보조 경로',
                    nodes: ['VWFA', '각회·연상회', '좌측 하전두회'],
                    description: '자소를 음소로 대응시키고 읽기 순서와 발화 준비를 보조합니다.'
                }
            ]
        },
        hanja: {
            title: '배쪽 문자 경로 우세 · 시공간 보조',
            channel: '복잡한 획·고공간주파수 · P 채널 기여가 상대적으로 큼',
            overview: '복잡한 표의문자 형태를 복측 후두측두피질에서 통합하고, 두정 영역의 시공간 분석을 거쳐 어휘·의미 인출 네트워크와 협력합니다.',
            note: '숙련도와 문자 복잡도에 따라 좌우 후두측두·두정·전두 영역의 상대적 관여가 크게 달라질 수 있습니다.',
            evidenceIds: ['visual-main', 'dual-stream', 'reading-hangul-hanja', 'word-form'],
            branches: [
                {
                    kind: 'ventral',
                    emphasis: 'primary',
                    badge: '주 경로',
                    label: '배쪽 문자·의미 경로',
                    nodes: ['V1', 'V2/V4', '양측 후두측두', 'VWFA·IT', '어휘·의미망'],
                    description: '복합 획을 학습된 문자 형태로 묶고 저장된 개념과 발음을 인출합니다.'
                },
                {
                    kind: 'dorsal',
                    emphasis: 'support',
                    badge: '보조 경로',
                    label: '시공간 분석 경로',
                    nodes: ['V1/V2', 'IPS·위마루소엽', '전두 언어망'],
                    description: '획의 위치 관계와 주의 이동을 계산해 복잡한 문자 판별을 돕습니다.'
                }
            ]
        },
        face: {
            title: '배쪽 얼굴 인식 경로 우세',
            channel: '얼굴 형태·시선 단서 · P·M 신호가 피질에서 통합',
            overview: '초기 시각 특징이 후두얼굴영역과 방추상얼굴영역으로 이어져 얼굴 구성과 정체성을 분석하고, 시선·표정 단서는 별도 사회·정서 네트워크와 병렬 처리됩니다.',
            note: '현재 이미지는 중립 표정의 정지 얼굴이므로 동적 표정이나 강한 위협 표정보다 pSTS·편도체 관여를 낮게 해석해야 합니다.',
            evidenceIds: ['visual-main', 'dual-stream', 'face'],
            branches: [
                {
                    kind: 'ventral',
                    emphasis: 'primary',
                    badge: '주 경로',
                    label: '배쪽 얼굴·정체성 경로',
                    nodes: ['V1', 'V2/V4', 'OFA', 'FFA', '전측두피질'],
                    description: '눈·입의 부분 특징을 얼굴 전체 배치와 사람의 정체성 표상으로 통합합니다.'
                },
                {
                    kind: 'salience',
                    emphasis: 'support',
                    badge: '병렬 보조',
                    label: '사회·정서 단서 경로',
                    nodes: ['OFA', 'pSTS', '편도체'],
                    description: '시선과 표정 변화, 장면의 생물학적 중요도를 상황에 맞게 평가합니다.'
                }
            ]
        },
        landscape: {
            title: '장면망 · 배쪽과 등쪽 병렬',
            channel: '전역 배치·깊이·경계 · P·M 신호가 병렬 기여',
            overview: '장소 정체성, 눈앞의 경계와 이동 가능성, 장기 장소 기억을 서로 연결된 장면 선택 영역들이 병렬로 처리합니다.',
            note: 'PPA·OPA·RSC/MPA는 하나의 직렬 선이 아니라 서로 다른 장면 정보를 주고받는 병렬 네트워크로 표시합니다.',
            evidenceIds: ['visual-main', 'dual-stream', 'place'],
            branches: [
                {
                    kind: 'ventral',
                    emphasis: 'primary',
                    badge: '병렬 주 경로',
                    label: '장면 정체성 경로',
                    nodes: ['V1', 'V2/V3', 'PPA'],
                    description: '전역 배치와 장소 범주를 분석해 이 장면이 어떤 곳인지 판별합니다.'
                },
                {
                    kind: 'dorsal',
                    emphasis: 'primary',
                    badge: '병렬 주 경로',
                    label: '공간·이동 경로',
                    nodes: ['V1/V3', 'OPA', '후두정피질'],
                    description: '눈앞의 경계와 깊이, 이동 가능한 방향을 행동 중심 좌표로 바꿉니다.'
                },
                {
                    kind: 'ventral',
                    emphasis: 'support',
                    badge: '기억 보조',
                    label: '장소 기억 경로',
                    nodes: ['V1/V2', 'RSC/MPA', '해마'],
                    description: '현재 장면을 익숙한 방향·장소 기억과 연결합니다.'
                }
            ]
        },
        motion: {
            title: '등쪽 움직임·행동 경로 우세',
            channel: '빠른 시간 변화·저공간주파수 · M 채널 기여가 상대적으로 큼',
            overview: '움직임에 민감한 MT/V5와 MST가 방향·속도·광류를 계산하고, 후두정피질이 이를 공간 주의와 행동 계획으로 변환합니다.',
            note: '현재 자산은 동영상이 아닌 정지 사진입니다. 배경 흐림과 자세가 암시하는 움직임을 모델링하며 실제 연속 운동 자극과 반응 크기는 다를 수 있습니다.',
            evidenceIds: ['visual-main', 'dual-stream', 'motion', 'motion-implied'],
            branches: [
                {
                    kind: 'dorsal',
                    emphasis: 'primary',
                    badge: '주 경로',
                    label: '등쪽 움직임·행동 경로',
                    nodes: ['V1', 'V2/V3A', 'MT/V5', 'MST', 'PPC·IPS'],
                    description: '암시된 이동 방향과 속도를 추정해 다음 위치와 행동 가능성을 계산합니다.'
                },
                {
                    kind: 'ventral',
                    emphasis: 'support',
                    badge: '보조 경로',
                    label: '움직이는 대상 인식',
                    nodes: ['V1', 'V2/V4', '하측두피질'],
                    description: '무엇이 움직이는지 대상의 형태와 범주를 함께 판별합니다.'
                }
            ]
        },
        danger: {
            title: '공간·정서 경로 혼합',
            channel: '접근·대비·장면 맥락 · M·P 채널이 병렬 기여',
            overview: '대상과 장면 맥락을 식별하는 배쪽 처리와 접근 방향을 계산하는 등쪽 처리가 편도체·전전두 조절망과 협력해 대응 우선순위를 정합니다.',
            note: '상구-시상베개(pulvinar)-편도체 경로는 빠른 경보의 후보 보조 경로입니다. 확정된 공포 전용선이나 유일한 위협 경로로 해석하지 않습니다.',
            evidenceIds: ['visual-main', 'dual-stream', 'threat-subcortical', 'amygdala'],
            branches: [
                {
                    kind: 'salience',
                    emphasis: 'primary',
                    badge: '병렬 주 경로',
                    label: '대상·맥락과 중요도 평가',
                    nodes: ['V1/V2', 'V4·IT', '편도체', '전전두피질'],
                    description: '위험 대상과 상황 맥락을 식별하고 생물학적 중요도와 대응 전략을 평가합니다.'
                },
                {
                    kind: 'dorsal',
                    emphasis: 'primary',
                    badge: '병렬 주 경로',
                    label: '접근·회피 공간 경로',
                    nodes: ['V1', 'MT/MST', '후두정피질', '전전두피질'],
                    description: '접근 방향과 충돌 가능성, 회피할 공간과 행동을 계산합니다.'
                },
                {
                    kind: 'salience',
                    emphasis: 'candidate',
                    badge: '후보 보조 · 점선',
                    label: '피질하 경보 후보 경로',
                    nodes: ['시삭', '상구', '시상베개', '편도체'],
                    description: '의식적 식별보다 앞선 거친 현저성 신호가 전달될 가능성을 보여주는 논쟁적 보조 모델입니다.'
                }
            ]
        },
        color: {
            title: '배쪽 색채·표면 경로 우세',
            channel: '원추세포·P/K 입력 기여 · 색 신호는 여러 경로에서 상호작용',
            overview: 'V1과 V2의 색 선택 반응이 V4/VO1과 복측 후두측두 영역으로 이어져 지각색, 색 항상성, 대상의 표면색과 의미를 분산 처리합니다.',
            note: 'V4를 하나의 단일 색 중추로 보지 않습니다. 색채는 V1·V2·V4/VO와 복측 후두측두 영역에 걸친 분산 네트워크에서 처리됩니다.',
            evidenceIds: ['visual-main', 'dual-stream', 'visual-specialization'],
            branches: [
                {
                    kind: 'ventral',
                    emphasis: 'primary',
                    badge: '주 경로',
                    label: '배쪽 색채·표면 경로',
                    nodes: ['V1 색 선택 영역', 'V2', 'V4/VO1', '복측 후두측두'],
                    description: '색 대비를 조명 변화에도 비교적 안정적인 표면색과 대상 정보로 통합합니다.'
                },
                {
                    kind: 'dorsal',
                    emphasis: 'support',
                    badge: '주의 보조',
                    label: '색 유도 시각 탐색',
                    nodes: ['V1/V2', '후두정피질'],
                    description: '목표 색이 있는 위치로 주의를 이동하고 탐색 행동을 지원합니다.'
                }
            ]
        }
    };

    public regions: BrainRegion[] = [
        { id: 'pfc', name: '전전두엽', english: 'Prefrontal cortex', short: 'PFC', network: '주의·판단', location: '전두엽 앞쪽', function: '목표 유지, 주의 조절, 판단과 행동 선택을 담당합니다.', evidenceId: 'word-network', lateralX: 18, lateralY: 36, dorsalX: 32, dorsalY: 23, pathwayX: 57, pathwayY: 18 },
        { id: 'broca', name: '브로카 영역', english: 'Broca area', short: 'BA', network: '언어 산출', location: '좌측 하전두회', function: '말소리 계획, 문법 처리, 발화 준비에 관여합니다.', evidenceId: 'word-network', lateralX: 25, lateralY: 53, dorsalX: 31, dorsalY: 38, pathwayX: 58, pathwayY: 34 },
        { id: 'parietal', name: '후두정엽', english: 'Posterior parietal cortex', short: 'PPC', network: '공간·주의', location: '두정엽 뒤쪽', function: '공간 위치, 시각적 주의, 행동 방향을 통합합니다.', evidenceId: 'motion', lateralX: 54, lateralY: 27, dorsalX: 35, dorsalY: 48, pathwayX: 75, pathwayY: 16 },
        { id: 'wernicke', name: '베르니케 영역', english: 'Wernicke area', short: 'WA', network: '언어 이해', location: '좌측 후상측두 영역', function: '단어와 문장의 소리·의미를 이해하는 데 관여합니다.', evidenceId: 'word-network', lateralX: 40, lateralY: 51, dorsalX: 31, dorsalY: 60, pathwayX: 72, pathwayY: 34 },
        { id: 'temporal', name: '하측두피질', english: 'Inferior temporal cortex', short: 'IT', network: '사물·의미', location: '측두엽 아래쪽', function: '복합 형태, 사물 범주, 시각 의미 기억을 표현합니다.', evidenceId: 'face', lateralX: 48, lateralY: 64, dorsalX: 29, dorsalY: 70, pathwayX: 69, pathwayY: 47 },
        { id: 'amygdala', name: '편도체', english: 'Amygdala', short: 'AMY', network: '정서·현저성', location: '내측 측두엽', function: '위협과 정서적 중요도를 빠르게 평가합니다.', evidenceId: 'amygdala', lateralX: 43, lateralY: 68, dorsalX: 38, dorsalY: 65, pathwayX: 64, pathwayY: 42 },
        { id: 'ffa', name: '방추상회 얼굴 영역', english: 'Fusiform face area', short: 'FFA', network: '얼굴 인식', location: '복측 측두엽', function: '얼굴의 전체 배치와 정체성 단서에 민감하게 반응합니다.', evidenceId: 'face', lateralX: 57, lateralY: 68, dorsalX: 34, dorsalY: 74, pathwayX: 76, pathwayY: 51 },
        { id: 'vwfa', name: '시각단어형태영역', english: 'Visual word form area', short: 'VWFA', network: '문자 인식', location: '좌측 후두측두 영역', function: '학습된 글자와 철자 패턴을 빠르게 인식합니다.', evidenceId: 'word-form', lateralX: 61, lateralY: 59, dorsalX: 31, dorsalY: 70, pathwayX: 81, pathwayY: 47 },
        { id: 'ppa', name: '해마방회 장소 영역', english: 'Parahippocampal place area', short: 'PPA', network: '장면 인식', location: '복측 측두엽 안쪽', function: '장면의 구조와 장소 정체성을 표현합니다.', evidenceId: 'place', lateralX: 66, lateralY: 55, dorsalX: 37, dorsalY: 66, pathwayX: 73, pathwayY: 43 },
        { id: 'mt', name: 'MT/V5', english: 'Middle temporal area', short: 'MT', network: '움직임', location: '외측 후두측두 영역', function: '움직임의 방향과 속도를 통합합니다.', evidenceId: 'motion', lateralX: 71, lateralY: 38, dorsalX: 29, dorsalY: 61, pathwayX: 82, pathwayY: 36 },
        { id: 'v4', name: '시각영역 V4', english: 'Visual area V4', short: 'V4', network: '색상·형태', location: '복측 후두엽', function: '색채, 곡률, 중간 수준 형태와 표면을 통합합니다.', evidenceId: 'visual-specialization', lateralX: 80, lateralY: 51, dorsalX: 34, dorsalY: 78, pathwayX: 86, pathwayY: 42 },
        { id: 'v1', name: '1차 시각피질', english: 'Primary visual cortex', short: 'V1', network: '초기 시각', location: '후두엽 후극', function: '경계, 방향, 국소 대비를 시각 지도에 배치합니다.', evidenceId: 'visual-specialization', lateralX: 89, lateralY: 46, dorsalX: 40, dorsalY: 86, pathwayX: 91, pathwayY: 25 }
    ];

    public evidenceSources: EvidenceSource[] = [
        {
            id: 'visual-specialization',
            title: 'A direct demonstration of functional specialization in human visual cortex',
            citation: 'Zeki et al. · Journal of Neuroscience · 1991',
            method: 'PET · 색채(V4)와 움직임(V5) 조건 비교',
            url: 'https://pubmed.ncbi.nlm.nih.gov/2002358/'
        },
        {
            id: 'motion',
            title: 'Functional analysis of human MT and related visual cortical areas using MRI',
            citation: 'Tootell et al. · Journal of Neuroscience · 1995',
            method: 'fMRI · 움직임·색상·대비 반응 비교',
            url: 'https://pubmed.ncbi.nlm.nih.gov/7722658/'
        },
        {
            id: 'face',
            title: 'The fusiform face area: a module specialized for face perception',
            citation: 'Kanwisher et al. · Journal of Neuroscience · 1997',
            method: 'fMRI · 얼굴과 사물·손·주택 조건 비교',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9151747/'
        },
        {
            id: 'place',
            title: 'A cortical representation of the local visual environment',
            citation: 'Epstein & Kanwisher · Nature · 1998',
            method: 'fMRI · 장면·사물·얼굴 조건 비교',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9560155/'
        },
        {
            id: 'word-form',
            title: 'Functional properties of the Visual Word Form Area',
            citation: 'Cohen et al. · Brain · 2002',
            method: 'fMRI · 단어·자음열·시각 패턴 비교',
            url: 'https://pubmed.ncbi.nlm.nih.gov/11960895/'
        },
        {
            id: 'word-network',
            title: 'Privileged functional connectivity between the VWFA and language system',
            citation: 'Stevens et al. · Journal of Neuroscience · 2017',
            method: 'fMRI · 단어 판별과 언어망 기능 연결성',
            url: 'https://pubmed.ncbi.nlm.nih.gov/28450544/'
        },
        {
            id: 'amygdala',
            title: 'Masked emotional facial expressions modulate amygdala activity',
            citation: 'Whalen et al. · Journal of Neuroscience · 1998',
            method: 'fMRI · 공포·행복 표정의 비의식적 제시',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9412517/'
        },
        {
            id: 'visual-main',
            title: 'Neuroanatomy, Visual Pathway',
            citation: 'StatPearls · NCBI Bookshelf',
            method: '망막·시신경·시각교차·시삭·LGN·시방사·V1 해부학',
            url: 'https://www.ncbi.nlm.nih.gov/books/NBK553189/'
        },
        {
            id: 'dual-stream',
            title: 'Separate visual pathways for perception and action',
            citation: 'Goodale & Milner · Trends in Neurosciences · 1992',
            method: '복측 지각·대상 인식과 배측 시각 유도 행동의 이중 경로 모델',
            url: 'https://pubmed.ncbi.nlm.nih.gov/1374953/'
        },
        {
            id: 'reading-hangul-hanja',
            title: 'Neural substrates of Hanja and Hangul character readings',
            citation: 'Cho et al. · Journal of Korean Medical Science · 2014',
            method: 'fMRI · 숙련 독자의 한글·한자 읽기 활성 패턴 비교',
            url: 'https://pubmed.ncbi.nlm.nih.gov/25368497/'
        },
        {
            id: 'motion-implied',
            title: 'Activation in human MT/MST by static images with implied motion',
            citation: 'Kourtzi & Kanwisher · Journal of Cognitive Neuroscience · 2000',
            method: 'fMRI · 움직임을 암시하는 정지 사진과 정적 사진 비교',
            url: 'https://pubmed.ncbi.nlm.nih.gov/10769305/'
        },
        {
            id: 'threat-subcortical',
            title: 'A subcortical pathway to the right amygdala mediating unseen fear',
            citation: 'Morris et al. · PNAS · 1999',
            method: 'fMRI 연결성 · 상구-시상베개-편도체 후보 경로',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9990084/'
        },
        {
            id: 'object-loc',
            title: 'Object-related activity revealed by functional magnetic resonance imaging in human occipital cortex',
            citation: 'Malach et al. · PNAS · 1995',
            method: 'fMRI · 온전한 물체와 분할 자극을 비교해 LOC의 물체 형태 통합 반응 규명',
            url: 'https://pubmed.ncbi.nlm.nih.gov/7667258/'
        },
        {
            id: 'ventral-hierarchy',
            title: 'A hierarchical axis of object processing stages in the human visual cortex',
            citation: 'Lerner et al. · Cerebral Cortex · 2001',
            method: 'fMRI 적응 · 인간 복측 시각피질의 후방-전방 물체 처리 계층',
            url: 'https://pubmed.ncbi.nlm.nih.gov/11278192/'
        },
        {
            id: 'object-concepts',
            title: 'Conceptual object representations in human anterior temporal cortex',
            citation: 'Peelen & Caramazza · Journal of Neuroscience · 2012',
            method: 'fMRI 다변량 분석 · 뒤쪽 지각 특징에서 앞쪽 용도·장소 개념 정보로의 변화',
            url: 'https://pubmed.ncbi.nlm.nih.gov/23136412/'
        },
        {
            id: 'eye-attention',
            title: 'Microstimulation of the frontal eye field and its effects on covert spatial attention',
            citation: 'Moore & Fallah · Journal of Neurophysiology · 2004',
            method: '원숭이 미세자극 · 안구운동 없이도 FEF가 선택 위치의 탐지 민감도를 높임',
            url: 'https://pubmed.ncbi.nlm.nih.gov/13679398/'
        },
        {
            id: 'stream-awareness',
            title: 'Differential modulation of visual object processing in dorsal and ventral stream by stimulus visibility',
            citation: 'Ludwig et al. · Cortex · 2016',
            method: 'fMRI · 배측과 복측 경로 모두 자극 가시성과 관련됨을 비교',
            url: 'https://pubmed.ncbi.nlm.nih.gov/27504609/'
        }
    ];

    public connections: BrainConnection[] = [
        { from: 'v1', to: 'v4', label: '형태·색상 경로' },
        { from: 'v1', to: 'mt', label: '움직임 경로' },
        { from: 'v4', to: 'vwfa', label: '문자 형태' },
        { from: 'v4', to: 'ffa', label: '얼굴 형태' },
        { from: 'v4', to: 'temporal', label: '사물 의미' },
        { from: 'vwfa', to: 'wernicke', label: '문자-언어 연결' },
        { from: 'wernicke', to: 'broca', label: '언어 네트워크' },
        { from: 'mt', to: 'parietal', label: '배측 시각 경로' },
        { from: 'ppa', to: 'parietal', label: '장면-공간 연결' },
        { from: 'ffa', to: 'amygdala', label: '표정-정서 연결' },
        { from: 'temporal', to: 'amygdala', label: '의미-현저성 연결' },
        { from: 'parietal', to: 'pfc', label: '주의-판단 연결' },
        { from: 'amygdala', to: 'pfc', label: '정서 조절 연결' }
    ];

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        await this.service.auth.allow('/access');
        this.selectTopRegion();
        await this.service.render();
    }

    public get selectedStimulus(): VisualStimulus {
        return this.stimuli.find(item => item.id === this.selectedStimulusId) || this.stimuli[0];
    }

    public get selectedFocus(): StimulusFocusPoint {
        return this.selectedStimulus.focusPoints.find(point => point.id === this.selectedFocusId)
            || this.selectedStimulus.focusPoints[0];
    }

    public get compareStimulus(): VisualStimulus {
        return this.stimuli.find(item => item.id === this.compareStimulusId) || this.stimuli[1];
    }

    public get selectedRegion(): BrainRegion {
        return this.regions.find(region => region.id === this.selectedRegionId) || this.regions[0];
    }

    public get selectedEvidence(): EvidenceSource {
        return this.evidenceSources.find(source => source.id === this.selectedRegion.evidenceId)
            || this.evidenceSources[0];
    }

    public get selectedPathway(): VisualPathwayProfile {
        return this.pathwayProfiles[this.selectedStimulusId] || this.pathwayProfiles.hangul;
    }

    public get selectedPathwayEvidence(): EvidenceSource[] {
        return this.selectedPathway.evidenceIds
            .map(evidenceId => this.evidenceSources.find(source => source.id === evidenceId))
            .filter(source => !!source) as EvidenceSource[];
    }

    public get objectProcessingEvidence(): EvidenceSource[] {
        return this.objectProcessingEvidenceIds
            .map(evidenceId => this.evidenceSources.find(source => source.id === evidenceId))
            .filter(source => !!source) as EvidenceSource[];
    }

    public get selectedFocusPositionLabel(): string {
        const horizontal = this.selectedFocus.x < 38
            ? '왼쪽'
            : (this.selectedFocus.x > 62 ? '오른쪽' : '가운데');
        const vertical = this.selectedFocus.y < 35
            ? '위쪽'
            : (this.selectedFocus.y > 65 ? '아래쪽' : '중앙');
        return horizontal + ' · ' + vertical + ' (' + Math.round(this.selectedFocus.x) + '%, ' + Math.round(this.selectedFocus.y) + '%)';
    }

    public get focusLensStyle(): any {
        return {
            'background-image': 'url("' + this.selectedStimulus.imageSrc + '")',
            'background-position': this.selectedFocus.x + '% ' + this.selectedFocus.y + '%'
        };
    }

    public get processingRecognitionRegion(): BrainRegion {
        const regionIdByStimulus: { [key: string]: string } = {
            hangul: 'vwfa',
            hanja: 'vwfa',
            face: 'ffa',
            landscape: 'ppa',
            motion: 'mt',
            danger: 'amygdala',
            color: 'v4'
        };
        const regionId = regionIdByStimulus[this.selectedStimulusId] || 'temporal';
        return this.regions.find(region => region.id === regionId)
            || this.regions.find(region => region.id === 'temporal')
            || this.regions[0];
    }

    public get processingOutcomeLabel(): string {
        const outcomeByStimulus: { [key: string]: string } = {
            hangul: '읽은 단어',
            hanja: '문자 의미',
            face: '얼굴·표정',
            landscape: '장소·장면',
            motion: '방향·속도',
            danger: '위험 판단',
            color: '색·형태'
        };
        return outcomeByStimulus[this.selectedStimulusId] || this.selectedStimulus.category;
    }

    public get processingActionLabel(): string {
        const actionByStimulus: { [key: string]: string } = {
            hangul: '다음 글자',
            hanja: '다음 획',
            face: '시선·표정',
            landscape: '이동 경로',
            motion: '다음 위치',
            danger: '회피·정지',
            color: '시선 이동'
        };
        return actionByStimulus[this.selectedStimulusId] || this.selectedFocus.cue;
    }

    public get hasSaliencePath(): boolean {
        return this.selectedStimulusId === 'danger' || this.selectedStimulusId === 'face';
    }

    public processingNodeActivation(nodeId: string): number {
        if (nodeId === 'eye') return 100;
        if (nodeId === 'lgn') return Math.max(64, this.activation('v1') - 6);
        if (nodeId === 'v1') return this.activation('v1');
        if (nodeId === 'v4') return this.activation('v4');
        if (nodeId === 'loc') {
            return this.averageActivation(['v4', 'temporal', this.processingRecognitionRegion.id]);
        }
        if (nodeId === 'it') {
            return this.averageActivation(['temporal', this.processingRecognitionRegion.id]);
        }
        if (nodeId === 'memory') {
            return this.averageActivation(['temporal', this.processingRecognitionRegion.id, 'pfc']);
        }
        if (nodeId === 'mt') return this.activation('mt');
        if (nodeId === 'parietal') return this.activation('parietal');
        if (nodeId === 'action') return this.averageActivation(['mt', 'parietal', 'pfc']);
        if (nodeId === 'amygdala') return this.activation('amygdala');
        return 0;
    }

    public processingNodeStyle(nodeId: string): any {
        const level = this.processingNodeActivation(nodeId);
        return {
            '--node-level': (level / 100).toFixed(2),
            '--node-angle': Math.round(level * 3.6) + 'deg',
            '--node-glow': Math.round(12 + level * 0.22) + 'px'
        };
    }

    public processingStreamStrength(kind: 'dorsal' | 'ventral' | 'salience'): number {
        if (kind === 'dorsal') return this.averageActivation(['mt', 'parietal', 'pfc']);
        if (kind === 'salience') return this.averageActivation(['amygdala', 'pfc']);
        return this.averageActivation(['v4', 'temporal', this.processingRecognitionRegion.id]);
    }

    public processingStreamWidth(kind: 'dorsal' | 'ventral' | 'salience'): number {
        return Math.round((4 + this.processingStreamStrength(kind) / 13) * 10) / 10;
    }

    public processingStreamOpacity(kind: 'dorsal' | 'ventral' | 'salience'): number {
        return Math.min(1, 0.42 + this.processingStreamStrength(kind) / 175);
    }

    private averageActivation(regionIds: string[]): number {
        if (!regionIds.length) return 0;
        const total = regionIds.reduce((sum, regionId) => sum + this.activation(regionId), 0);
        return Math.round(total / regionIds.length);
    }

    public recognitionFocusDetail(stageId: RecognitionStageId): string {
        if (stageId === 'features') return this.selectedFocus.processing.features;
        if (stageId === 'loc') return this.selectedFocus.processing.form;
        if (stageId === 'it') {
            return '‘' + this.selectedFocus.label + '’에 대한 복잡한 특징 조합을 '
                + this.selectedStimulus.category + ' 맥락의 범주·정체성 후보로 분산 표현합니다.';
        }
        return this.selectedFocus.processing.memory;
    }

    public get rankedRegions(): BrainRegion[] {
        return [...this.regions].sort((a, b) => this.activation(b.id) - this.activation(a.id));
    }

    public get activationHotspots(): BrainRegion[] {
        return this.rankedRegions.slice(0, 3);
    }

    public get focusShiftRegions(): BrainRegion[] {
        return [...this.regions]
            .filter(region => this.focusDelta(region.id) !== 0)
            .sort((a, b) => Math.abs(this.focusDelta(b.id)) - Math.abs(this.focusDelta(a.id)))
            .slice(0, 3);
    }

    public get comparisonRegions(): BrainRegion[] {
        return [...this.regions]
            .sort((a, b) => {
                const bMax = Math.max(this.activation(b.id), this.compareActivation(b.id));
                const aMax = Math.max(this.activation(a.id), this.compareActivation(a.id));
                return bMax - aMax;
            })
            .slice(0, 8);
    }

    public get activationReason(): string {
        return this.selectedStimulus.reasons[this.selectedRegion.id]
            || this.selectedRegion.function + ' 이 과제에서는 보조적인 네트워크로 관여합니다.';
    }

    public baselineActivation(regionId: string): number {
        return this.selectedStimulus.activations[regionId] || 0;
    }

    public activation(regionId: string): number {
        const value = this.baselineActivation(regionId) + (this.selectedFocus.activationDeltas[regionId] || 0);
        return Math.max(0, Math.min(100, value));
    }

    public compareActivation(regionId: string): number {
        return this.compareStimulus.activations[regionId] || 0;
    }

    public focusDelta(regionId: string): number {
        return this.activation(regionId) - this.baselineActivation(regionId);
    }

    public focusDeltaLabel(regionId: string): string {
        const value = this.focusDelta(regionId);
        if (value > 0) return '+' + value;
        if (value === 0) return '±0';
        return String(value);
    }

    public focusDeltaClass(regionId: string): string {
        const value = this.focusDelta(regionId);
        if (value > 0) return 'is-up';
        if (value < 0) return 'is-down';
        return 'is-steady';
    }

    public regionById(regionId: string): BrainRegion {
        return this.regions.find(region => region.id === regionId) || this.regions[0];
    }

    public heatColor(value: number): string {
        if (value >= 90) return '#e11d48';
        if (value >= 75) return '#f97316';
        if (value >= 60) return '#f59e0b';
        if (value >= 45) return '#14b8a6';
        return '#64748b';
    }

    public surfaceHeatmap(view: 'lateral' | 'dorsal'): string {
        const layers: string[] = [];
        const ranked = [...this.regions].sort((a, b) => this.activation(b.id) - this.activation(a.id));

        ranked.forEach(region => {
            const value = this.activation(region.id);
            if (value < 12) return;

            const x = view === 'lateral' ? region.lateralX : region.dorsalX;
            const y = view === 'lateral' ? region.lateralY : region.dorsalY;
            layers.push(this.surfaceGradient(value, x, y, view));

            if (view === 'dorsal' && this.isBilateralRegion(region.id) && Math.abs(50 - x) > 7) {
                layers.push(this.surfaceGradient(value, 100 - x, y, view));
            }
        });

        layers.push('linear-gradient(135deg, rgba(30, 64, 175, 0.78) 0%, rgba(29, 78, 216, 0.72) 28%, rgba(8, 47, 73, 0.82) 58%, rgba(5, 46, 22, 0.76) 100%)');
        return layers.join(', ');
    }

    public surfaceHotspotStyle(region: BrainRegion, view: 'lateral' | 'dorsal'): any {
        const x = view === 'lateral' ? region.lateralX : region.dorsalX;
        const y = view === 'lateral' ? region.lateralY : region.dorsalY;
        return {
            left: x + '%',
            top: y + '%'
        };
    }

    public focusPointStyle(point: StimulusFocusPoint): any {
        return {
            left: point.x + '%',
            top: point.y + '%'
        };
    }

    public pathwayHotspotStyle(region: BrainRegion, rank: number): any {
        const value = this.activation(region.id);
        const size = 36 + Math.round(value * 0.14);
        return {
            left: region.pathwayX + '%',
            top: region.pathwayY + '%',
            width: size + 'px',
            height: size + 'px',
            color: this.heatColor(value),
            zIndex: 8 - rank
        };
    }

    private surfaceGradient(value: number, x: number, y: number, view: 'lateral' | 'dorsal'): string {
        const width = view === 'lateral' ? 13 + value * 0.11 : 11 + value * 0.09;
        const height = view === 'lateral' ? 16 + value * 0.12 : 13 + value * 0.10;

        if (value >= 90) {
            return 'radial-gradient(ellipse ' + width + '% ' + height + '% at ' + x + '% ' + y + '%, rgba(254, 249, 195, 0.98) 0%, rgba(250, 204, 21, 0.98) 18%, rgba(249, 115, 22, 0.94) 42%, rgba(220, 38, 38, 0.78) 67%, transparent 100%)';
        }
        if (value >= 75) {
            return 'radial-gradient(ellipse ' + width + '% ' + height + '% at ' + x + '% ' + y + '%, rgba(253, 186, 116, 0.96) 0%, rgba(239, 68, 68, 0.92) 32%, rgba(126, 34, 206, 0.68) 66%, transparent 100%)';
        }
        if (value >= 58) {
            return 'radial-gradient(ellipse ' + width + '% ' + height + '% at ' + x + '% ' + y + '%, rgba(74, 222, 128, 0.94) 0%, rgba(20, 184, 166, 0.88) 38%, rgba(37, 99, 235, 0.64) 72%, transparent 100%)';
        }
        return 'radial-gradient(ellipse ' + width + '% ' + height + '% at ' + x + '% ' + y + '%, rgba(34, 211, 238, 0.78) 0%, rgba(37, 99, 235, 0.72) 42%, rgba(30, 58, 138, 0.46) 72%, transparent 100%)';
    }

    private isBilateralRegion(regionId: string): boolean {
        return ['broca', 'wernicke', 'vwfa'].indexOf(regionId) === -1;
    }

    public nodeRadius(regionId: string): number {
        return 13 + this.activation(regionId) / 12;
    }

    public compareRadius(regionId: string): number {
        return 17 + this.compareActivation(regionId) / 11;
    }

    public regionOpacity(regionId: string): number {
        return Math.min(0.98, 0.34 + this.activation(regionId) / 140);
    }

    public connectionOpacity(connection: BrainConnection): number {
        const average = (this.activation(connection.from) + this.activation(connection.to)) / 2;
        return Math.max(0.16, average / 100);
    }

    public delta(regionId: string): number {
        return this.activation(regionId) - this.compareActivation(regionId);
    }

    public deltaLabel(regionId: string): string {
        const value = this.delta(regionId);
        if (value > 0) return '+' + value;
        return String(value);
    }

    public deltaClass(regionId: string): string {
        const value = this.delta(regionId);
        if (value > 0) return 'text-rose-600';
        if (value < 0) return 'text-sky-600';
        return 'text-zinc-400';
    }

    public stimulusButtonClass(stimulusId: string): string {
        const base = 'stimulus-option group rounded-xl border text-left transition-all';
        if (this.selectedStimulusId === stimulusId) {
            return base + ' border-zinc-950 bg-zinc-950 text-white shadow-md';
        }
        return base + ' border-zinc-200 bg-white text-zinc-700 hover:-translate-y-0.5 hover:border-zinc-400 hover:shadow-sm';
    }

    public compareButtonClass(stimulusId: string): string {
        const base = 'rounded-lg border px-3 py-2 text-left transition-colors';
        if (this.compareStimulusId === stimulusId) {
            return base + ' border-sky-600 bg-sky-50 text-sky-800';
        }
        return base + ' border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400';
    }

    public viewButtonClass(mode: '2d' | '3d'): string {
        if (this.viewMode === mode) {
            return 'bg-zinc-950 text-white';
        }
        return 'bg-white text-zinc-600 hover:bg-zinc-50';
    }

    public brainTransform(): string {
        if (this.viewMode === '2d') return 'none';
        return 'perspective(1100px) rotateY(' + this.brainAngle + 'deg) rotateX(-3deg)';
    }

    public async setStimulus(stimulusId: string) {
        this.selectedStimulusId = stimulusId;
        this.resetFocus();
        if (this.compareStimulusId === stimulusId) {
            const alternative = this.stimuli.find(item => item.id !== stimulusId);
            this.compareStimulusId = alternative ? alternative.id : stimulusId;
        }
        this.selectTopRegion();
        await this.service.render();
    }

    public async setCompareStimulus(stimulusId: string) {
        if (stimulusId === this.selectedStimulusId) return;
        this.compareStimulusId = stimulusId;
        await this.service.render();
    }

    public async swapStimuli() {
        const previous = this.selectedStimulusId;
        this.selectedStimulusId = this.compareStimulusId;
        this.compareStimulusId = previous;
        this.resetFocus();
        this.selectTopRegion();
        await this.service.render();
    }

    public async selectFocus(focusId: string, event?: Event) {
        if (event) event.stopPropagation();
        if (!this.selectedStimulus.focusPoints.some(point => point.id === focusId)) return;
        this.selectedFocusId = focusId;
        this.selectTopRegion();
        await this.service.render();
    }

    public async selectFocusFromImage(event: MouseEvent) {
        const target = event.currentTarget as HTMLElement;
        const bounds = target.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;

        const x = ((event.clientX - bounds.left) / bounds.width) * 100;
        const y = ((event.clientY - bounds.top) / bounds.height) * 100;
        const nearest = this.selectedStimulus.focusPoints.reduce((best, point) => {
            const bestDistance = Math.pow(best.x - x, 2) + Math.pow(best.y - y, 2);
            const pointDistance = Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2);
            return pointDistance < bestDistance ? point : best;
        });

        await this.selectFocus(nearest.id);
    }

    public async selectRegion(regionId: string) {
        this.selectedRegionId = regionId;
        await this.service.render();
    }

    public async setViewMode(mode: '2d' | '3d') {
        this.viewMode = mode;
        if (mode === '2d') this.brainAngle = -10;
        await this.service.render();
    }

    public async rotateBrain(amount: number) {
        this.brainAngle = Math.max(-28, Math.min(28, this.brainAngle + amount));
        await this.service.render();
    }

    public async toggleComparison() {
        this.comparisonEnabled = !this.comparisonEnabled;
        await this.service.render();
    }

    public async toggleConnections() {
        this.showConnections = !this.showConnections;
        await this.service.render();
    }

    private resetFocus() {
        const firstFocus = this.selectedStimulus.focusPoints[0];
        this.selectedFocusId = firstFocus ? firstFocus.id : '';
    }

    private selectTopRegion() {
        const top = this.rankedRegions[0];
        this.selectedRegionId = top ? top.id : 'v1';
    }
}

export default PageDashboardComponent;